// ============================================================================
// SYNC FOLDER SERVICE
// ----------------------------------------------------------------------------
// Local project-folder access. Normal pipeline saves read from the selected
// folder but do not write intermediate artefacts into it. Explicit user output
// actions (for example saving a report or the final quote PDF) own their own
// write paths via the File System Access API.
//
// The sync folder handle is stored in IndexedDB (Dexie app_settings table,
// key: 'syncFolderHandle'). Chrome 122+ supports structured cloning of
// FileSystemDirectoryHandle, so the handle persists across page reloads.
// However, the browser may revoke permission between sessions. Ordinary
// evidence access re-requests READ permission only; explicit user save/export
// actions request READWRITE at the point where they write.
//
// Folder structure (2026-05-20+, manifest schema v5 — de-bloat):
//   <SyncFolder>/...any customer hierarchy.../<existing project folder>/
//     ├── QuoteData.json       (source of truth — includes documents[i].content)
//     ├── SyncManifest.json    (v5 file inventory for restore)
//     ├── Project_Index.md
//     ├── Inspector_Notes.txt  (when project has notes)
//     ├── Risk_Report.txt      (when risk analysis exists)
//     └── AI_Learnings.json    (when learnings exist — promoted from learnings/)
//
// What changed in v5.37.9 (manifest schema v5): sync NO LONGER duplicates
// originals into the project folder. We previously wrote:
//   photos/<photoN>.jpg                  — duplicated user-imported JPEGs
//   documents/<docN>.pdf                 — duplicated user-imported PDFs
//   documents/<docN>_extracted.txt       — text already in QuoteData.json
//   videos/<vidN>.mp4                    — duplicated user-imported videos
//   videos/<vidN>_transcript.txt         — transcript already in QuoteData.json
//   learnings/AI_Learnings.json          — same JSON, just nested one level
// All of these are gone from new syncs. Originals live where the user put them
// (intake folder / FileExplorer source). QuoteData.json carries
// documents[i].content (extracted text) for downstream pipelines.
//
// Non-destructive: existing v4-layout folders on disk (Rodney's v5.37.4 box)
// keep their photos/ + documents/ + videos/ + learnings/ subfolders — sync
// just stops writing new ones. Restore path is back-compat with v4 manifests
// (subfolder reads tolerate ENOENT gracefully).
//
// Caveat: restoring a project on a FRESH device now requires the originals
// to be present at their original source path (FileExplorer import). The
// QuoteData.json carries metadata + extracted text but not the binary blobs.
// ============================================================================

import db from './db';
import { FileSystemService } from './file-system';
import { buildProjectIndexMarkdown, buildSyncFolderName } from './sync-folder-naming';
import {
    buildVideoStoragePolicy,
    VIDEO_ORIGINAL_STORAGE,
} from './video-storage-policy';
import {
    getFolderAccessUnsupportedMessage,
    hasDirectoryPicker,
    normaliseDirectoryPickerError,
} from './folder-access-support';
import { isDocumentDerivedVisual } from '../utils/project-photo-contract';
import { requireVerifiedTenantRuntimeId } from './tenant-runtime-scope';

function isBlobLike(value) {
    return typeof Blob !== 'undefined' && value instanceof Blob;
}

function isPdfDocument(doc) {
    const name = String(doc?.name || doc?.originalName || '').toLowerCase();
    const ext = String(doc?.ext || name.split('.').pop() || '').toLowerCase();
    const mime = String(doc?.contentType || doc?.mimeType || '').split(';')[0].trim().toLowerCase();
    return ext === 'pdf' || name.endsWith('.pdf') || mime === 'application/pdf';
}

function normalizePdfBlob(blob) {
    if (!isBlobLike(blob) || blob.size <= 0) return null;
    return blob.type === 'application/pdf'
        ? blob
        : blob.slice(0, blob.size, 'application/pdf');
}

function createPdfObjectUrl(file) {
    if (!isBlobLike(file) || typeof URL === 'undefined' || typeof URL.createObjectURL !== 'function') return null;
    const blob = normalizePdfBlob(file);
    return blob ? URL.createObjectURL(blob) : null;
}

// DEC-COUNCIL-007 / Candidate C5 Slice 2: consolidated to file-system-errors.js.
// Re-exported locally so the existing call sites in this file keep working
// without churn.
import { isTransientFileSystemError } from './file-system-errors.js';

// DEC-COUNCIL-007 / Candidate C5 Slice 1: manifest schema owner.
import { buildSyncManifestV4 } from './sync-folder-manifest.js';
// buildVideoStoragePolicy + VIDEO_ORIGINAL_STORAGE already imported above.

// DEC-COUNCIL-007 / Candidate C5 Slice 3: write engine owner. Retry tuning +
// transient-error classification lives there; this file shimms the original
// signature so existing call sites stay clean.
import {
    writeSyncFileWithRetry as _writeSyncFileWithRetryEngine,
    refreshDirectoryHandleState as _refreshDirectoryHandleStateEngine,
    childFileRetryDelay as _childFileRetryDelayEngine,
    projectDirectoryRetryDelay as _projectDirectoryRetryDelayEngine,
} from './sync-folder-write-engine.js';

function syncFailureReason(err, fallback = 'Unknown sync error') {
    return String(err?.message || err?.name || fallback);
}

function makeSyncFailure({ index = null, name = null, fileName = null, phase = null, err = null, reason = null } = {}) {
    return {
        index,
        name,
        fileName,
        phase,
        reason: reason || syncFailureReason(err),
        transient: err ? isTransientFileSystemError(err) : false,
    };
}

function buildSyncErrorMessage({ photoSyncFailures = [], documentSyncFailures = [], videoSyncFailures = [], dataSyncFailures = [] } = {}) {
    const parts = [];
    if (photoSyncFailures.length > 0) parts.push(`${photoSyncFailures.length} photo file(s)`);
    if (documentSyncFailures.length > 0) parts.push(`${documentSyncFailures.length} document file(s)`);
    if (videoSyncFailures.length > 0) parts.push(`${videoSyncFailures.length} video file(s)`);
    if (dataSyncFailures.length > 0) parts.push(`${dataSyncFailures.length} project data file(s)`);
    if (parts.length === 0) return null;
    return `Local sync partially completed: ${parts.join(', ')} could not be confirmed in the sync folder. The project save is intact. Reconnect the sync folder if this repeats.`;
}

function delay(ms) {
    return new Promise(resolve => setTimeout(resolve, ms));
}

// DEC-COUNCIL-007 / Candidate C5 Slice 3: retry primitives now live in
// sync-folder-write-engine.js. These local thin wrappers preserve the call-
// site signature (positional `directoryHandle, fileName, blob, opts`) so the
// 8+ internal callers in this file don't churn in the same commit.
const projectDirectoryRetryDelay = _projectDirectoryRetryDelayEngine;
const childFileRetryDelay = _childFileRetryDelayEngine;
const refreshDirectoryHandleState = _refreshDirectoryHandleStateEngine;

async function writeSyncFileWithRetry(directoryHandle, fileName, blob, opts = {}) {
    // Returns the engine's `{ result, directoryHandle, recovered }` shape —
    // callers in this file read writeResult.directoryHandle to update their
    // local sub-directory handle after a transient-error recovery, so we
    // must NOT unwrap.
    return _writeSyncFileWithRetryEngine({
        directoryHandle,
        fileName,
        blob,
        writeFileToDirectory: FileSystemService.writeFileToDirectory.bind(FileSystemService),
        ...opts,
    });
}

function logSyncFailure(message, err) {
    if (isTransientFileSystemError(err)) {
        const friendlyMessage = String(message || '[SyncFolder] File sync deferred:')
            .replace(/failed after retry/gi, 'deferred after Chrome folder refresh attempts')
            .replace(/sync failed/gi, 'sync deferred');
        console.info(friendlyMessage, `${err?.message || 'Chrome reported a temporary folder-state race.'} Project save is intact.`);
    } else {
        console.warn(message, err?.message);
    }
}

// In-memory cache - avoids hitting IndexedDB on every save
let _cachedHandle = null;
let _permissionGranted = false;
let _cachedHandleTenantId = null;

// Cross-tab cache invalidation (folder-sync audit fix, 2026-05-11). Without
// this, two tabs each cache their own _cachedHandle on first getHandle();
// when tab A re-picks a folder via Settings (or File Explorer), tab B's
// cache stays pointed at the OLD handle and the operator's writes go to the
// wrong place. BroadcastChannel emits a 'invalidate-handle' on every
// setHandle/clearHandle so every other tab nulls its cache and re-reads
// Dexie on the next getHandle() call.
const SYNC_FOLDER_CHANNEL_NAME_BASE = 'qm-sync-folder';
let _syncFolderChannel = null;
let _syncFolderChannelTenantId = null;

function _isCurrentTenantScope(tenantId) {
    try {
        return requireVerifiedTenantRuntimeId() === tenantId;
    } catch {
        return false;
    }
}

function _activateTenantScope() {
    const tenantId = requireVerifiedTenantRuntimeId();
    if (_cachedHandleTenantId !== tenantId) {
        _cachedHandle = null;
        _permissionGranted = false;
        _cachedHandleTenantId = tenantId;
    }
    if (_syncFolderChannel && _syncFolderChannelTenantId !== tenantId) {
        try { _syncFolderChannel.close(); } catch { /* non-fatal */ }
        _syncFolderChannel = null;
        _syncFolderChannelTenantId = null;
    }
    return tenantId;
}

function _getSyncFolderChannel(tenantId) {
    if (typeof BroadcastChannel === 'undefined') return null;
    if (!_isCurrentTenantScope(tenantId)) return null;
    if (_syncFolderChannel && _syncFolderChannelTenantId === tenantId) return _syncFolderChannel;
    try {
        _syncFolderChannel = new BroadcastChannel(`${SYNC_FOLDER_CHANNEL_NAME_BASE}::${tenantId}`);
        _syncFolderChannelTenantId = tenantId;
        _syncFolderChannel.addEventListener('message', (ev) => {
            if (!_isCurrentTenantScope(tenantId)) return;
            if (ev?.data?.tenantId !== tenantId) return;
            const kind = ev?.data?.kind;
            if (kind === 'invalidate-handle') {
                _cachedHandle = null;
                _permissionGranted = false;
            }
        });
    } catch {
        _syncFolderChannel = null;
        _syncFolderChannelTenantId = null;
    }
    return _syncFolderChannel;
}
function _broadcastCacheInvalidation(kind, tenantId) {
    if (!_isCurrentTenantScope(tenantId)) return;
    const ch = _getSyncFolderChannel(tenantId);
    if (!ch) return;
    try {
        ch.postMessage({ tenantId, kind, ts: Date.now() });
    } catch { /* channel closed; non-fatal */ }
}

// Legacy v5.19.11 wrapper name. QuoteMaker no longer creates this folder;
// it only reads it when a previously synced project already lives there.
// Modern sync writes into the existing customer/job folder, wherever it sits
// under the selected sync root.
const PROJECTS_SUBFOLDER_NAME = 'QuoteMaker Projects';
const MAX_PROJECT_DIRECTORY_SCAN_DEPTH = 6;
const MAX_PROJECT_DIRECTORY_SCAN_COUNT = 1200;
// FE-MOVED-1 (Peter 2026-06-13): videos are LOCAL_SYNC_REFERENCE only — since
// the v5.37.9 de-bloat, sync no longer writes originals into the project's
// videos/ subfolder, so recovery must look at the *import source* instead. We
// search the configured sync root's subtree for the video filename (bounded so
// a huge OneDrive/Dropbox tree can't stall the recovery pass). Smaller budgets
// than the project-folder scan above because this is a best-effort fallback,
// not the primary lookup.
const MAX_VIDEO_SOURCE_SCAN_DEPTH = 8;
const MAX_VIDEO_SOURCE_SCAN_COUNT = 4000;
const SYNC_SCAN_IGNORED_DIRECTORY_NAMES = new Set([
    '.git',
    '.svn',
    '.hg',
    'node_modules',
    'dist',
    'build',
]);

function normaliseSyncRelativePath(path = '') {
    return String(path || '')
        .replace(/\\/g, '/')
        .split('/')
        .map(part => part.trim())
        .filter(Boolean)
        .join('/');
}

function joinSyncRelativePath(basePath = '', name = '') {
    const base = normaliseSyncRelativePath(basePath);
    const leaf = String(name || '').trim();
    if (!base) return leaf;
    if (!leaf) return base;
    return `${base}/${leaf}`;
}

const RESTORE_IMAGE_EXTENSION_RE = /\.(?:jpg|jpeg|jfif|png|gif|webp|heic|heif|bmp|tif|tiff)$/i;
const RESTORE_TEXT_EXTENSION_RE = /\.txt$/i;
const RESTORE_VIDEO_EXTENSION_RE = /\.(?:mp4|mov|webm|avi)$/i;
const RESTORE_UUID_RE = /^[0-9a-f]{8}-[0-9a-f]{4}-[1-8][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i;
const MAX_RESTORE_FILE_SCAN_DEPTH = 10;
const MAX_RESTORE_FILE_SCAN_COUNT = 2500;

function createRestoreProjectId(...candidates) {
    const existing = candidates
        .map(value => String(value || '').trim())
        .find(value => RESTORE_UUID_RE.test(value));
    if (existing) return existing;
    if (globalThis.crypto?.randomUUID) return globalThis.crypto.randomUUID();

    // RFC 4122 v4 fallback for older Chromium/WebView builds.
    return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, token => {
        const random = Math.floor(Math.random() * 16);
        const value = token === 'x' ? random : ((random & 0x3) | 0x8);
        return value.toString(16);
    });
}

function restoreFileExtension(name = '') {
    const match = String(name || '').toLowerCase().match(/\.([a-z0-9]+)$/);
    return match?.[1] || '';
}

function normaliseRestoreFileToken(name = '') {
    let stem = String(name || '')
        .split(/[\\/]/)
        .pop()
        .replace(/\.[^.]+$/, '')
        .replace(/_extracted$/i, '');
    stem = /^photo[\s_-]*0*\d+(?:[\s_-]|$)/i.test(stem)
        ? stem.replace(/^photo[\s_-]*0*\d+[\s_-]*/i, '')
        : stem.replace(/^0*\d+[\s_-]*/i, '');
    return stem
        .toLowerCase()
        .replace(/[^a-z0-9]+/g, '');
}

function restorePhotoOrdinal(name = '') {
    const leaf = String(name || '').split(/[\\/]/).pop();
    const match = leaf.match(/^(?:photo[\s_-]*)?0*(\d+)(?:[\s_-]|$)/i);
    return match ? Number(match[1]) : null;
}

function stripExtractedTextHeader(text = '') {
    const source = String(text || '');
    const marker = source.indexOf('===');
    if (marker < 0) return source;
    const headerEnd = source.indexOf('\n\n', marker);
    return headerEnd >= 0 ? source.substring(headerEnd + 2) : source;
}

async function collectRestoreFileEntries(rootHandle) {
    const entries = [];
    let visitedDirectories = 0;

    const walk = async (dirHandle, relativePath, depth) => {
        if (!dirHandle
            || depth > MAX_RESTORE_FILE_SCAN_DEPTH
            || entries.length >= MAX_RESTORE_FILE_SCAN_COUNT
            || visitedDirectories >= MAX_RESTORE_FILE_SCAN_COUNT
        ) {
            return;
        }
        visitedDirectories += 1;

        let children = [];
        try {
            if (typeof dirHandle.values === 'function') {
                for await (const entry of dirHandle.values()) children.push(entry);
            } else if (typeof dirHandle.entries === 'function') {
                for await (const [, entry] of dirHandle.entries()) children.push(entry);
            }
        } catch {
            return;
        }

        children.sort((a, b) => String(a?.name || '').localeCompare(String(b?.name || '')));
        for (const entry of children) {
            if (!entry || entries.length >= MAX_RESTORE_FILE_SCAN_COUNT) break;
            const entryPath = joinSyncRelativePath(relativePath, entry.name);
            if (entry.kind === 'file') {
                entries.push({
                    handle: entry,
                    name: String(entry.name || entryPath.split('/').pop() || ''),
                    relativePath: entryPath,
                });
                continue;
            }
            if (entry.kind !== 'directory') continue;
            if (SYNC_SCAN_IGNORED_DIRECTORY_NAMES.has(String(entry.name || '').toLowerCase())) continue;
            await walk(entry, entryPath, depth + 1);
        }
    };

    await walk(rootHandle, '', 0);
    return entries;
}

function scoreRestoreFile(entry, expectedNames = [], options = {}) {
    const candidateName = String(entry?.name || '');
    const candidateLower = candidateName.toLowerCase();
    const candidateToken = normaliseRestoreFileToken(candidateName);
    const candidateExtension = restoreFileExtension(candidateName);
    const expected = expectedNames.map(value => String(value || '').trim()).filter(Boolean);
    let score = 0;

    for (const expectedName of expected) {
        const expectedLower = expectedName.split(/[\\/]/).pop().toLowerCase();
        const expectedToken = normaliseRestoreFileToken(expectedName);
        const expectedExtension = restoreFileExtension(expectedName);
        if (candidateLower === expectedLower) score = Math.max(score, 1400);
        if (candidateToken && candidateToken === expectedToken) score = Math.max(score, 1050);
        if (candidateToken.length >= 6 && expectedToken.length >= 6
            && (candidateToken.includes(expectedToken) || expectedToken.includes(candidateToken))
        ) {
            score = Math.max(score, 800);
        }
        if (expectedExtension && candidateExtension === expectedExtension) score += 120;
    }

    if (options.ordinal && restorePhotoOrdinal(candidateName) === options.ordinal) score += 600;

    const path = String(entry?.relativePath || '').toLowerCase();
    if (options.kind === 'photo') {
        if (path.startsWith('photos/')) score += 90;
        if (path.includes('99. qm/')) score += 70;
    } else if (options.kind === 'document') {
        if (path.startsWith('documents/')) score += 90;
        if (path.includes('99. qm/')) score += 30;
    } else if (options.kind === 'text' && path.includes('99. qm/')) {
        score += 90;
    }

    return score;
}

function selectBestRestoreFile(entries, expectedNames, options = {}, usedEntries = null) {
    let best = null;
    let bestScore = 0;
    for (const entry of entries) {
        if (usedEntries?.has(entry)) continue;
        const score = scoreRestoreFile(entry, expectedNames, options);
        if (score > bestScore) {
            best = entry;
            bestScore = score;
        }
    }
    const minimumScore = options.minScore
        ?? (options.kind === 'photo' ? 600 : 700);
    return bestScore >= minimumScore ? best : null;
}

export const SyncFolderService = {
    /**
     * Check if File System Access API is available (Chrome/Edge only).
     */
    isSupported() {
        return hasDirectoryPicker();
    },

    getUnsupportedMessage(feature = 'Folder sync') {
        return getFolderAccessUnsupportedMessage(feature);
    },

    /**
     * Retrieve the stored folder handle (from cache or IndexedDB).
     * Returns null if not configured.
     */
    async getHandle() {
        let tenantId;
        try {
            tenantId = _activateTenantScope();
        } catch {
            return null;
        }
        _getSyncFolderChannel(tenantId);
        if (_cachedHandle) return _cachedHandle;
        try {
            const record = await db.app_settings.get('syncFolderHandle');
            if (record?.value && _isCurrentTenantScope(tenantId)) {
                _cachedHandle = record.value;
                return _cachedHandle;
            }
        } catch (err) {
            console.warn('[SyncFolder] Failed to read handle from DB:', err?.message);
        }
        return null;
    },

    /**
     * Store a new folder handle (from showDirectoryPicker).
     *
     * Per file-explorer-redesign DEC-FE-008 §3.1 + Council seat #4
     * (PA-CP-2 re-engagement, 2026-05-11): also dual-writes the handle
     * into the file-explorer index (fe_root). Symmetric with
     * clearHandle's cascade so the index mirror stays consistent
     * without waiting for the next explorer open. Best-effort —
     * legacy app_settings is the source of truth, so a failed mirror
     * write is logged but does not propagate.
     */
    async setHandle(handle) {
        if (!handle) throw new Error('No sync folder handle was provided.');
        const tenantId = _activateTenantScope();
        _getSyncFolderChannel(tenantId);
        const previousHandle = _cachedHandle;
        const previousPermission = _permissionGranted;
        try {
            await db.app_settings.put({ key: 'syncFolderHandle', value: handle });
            if (!_isCurrentTenantScope(tenantId)) {
                throw new Error('The authenticated tenant changed while the sync folder was being saved.');
            }
            _cachedHandle = handle;
            _permissionGranted = true;
            // Tell other tabs to drop their _cachedHandle so they pick up
            // the new handle on the next getHandle() (cross-tab race fix).
            _broadcastCacheInvalidation('invalidate-handle', tenantId);
        } catch (err) {
            console.error('[SyncFolder] Failed to store handle:', err?.message);
            _cachedHandle = previousHandle;
            _permissionGranted = previousPermission;
            throw new Error('Could not save the sync folder permission in this browser. Please try again.');
        }

        // Mirror into file-explorer index. addRoot is idempotent (matches
        // existing fe_root by label), so repeated setHandle calls don't
        // create duplicate rows. Lazy import keeps the module graph identical
        // to pre-Phase-A for all other call sites.
        //
        // PB-CP-1 (file-explorer-redesign 2026-05-11): also register the
        // FileSystemObserver on the new handle (gesture-gated primary path
        // per Council seat #4 PB-CP-0 hybrid). Best-effort — observer is
        // a cache-freshener, not load-bearing for the explorer's render.
        try {
            const { addRoot } = await import('./file-explorer-index');
            const rootId = await addRoot(handle);
            if (rootId !== null) {
                const { registerObserverForRoot } = await import('./file-explorer-observer');
                await registerObserverForRoot(rootId, handle);
            }
        } catch (err) {
            console.warn('[SyncFolder] file-explorer index mirror / observer registration failed (best-effort):', err?.message);
        }
    },

    /**
     * Clear the stored folder handle.
     *
     * Per file-explorer-redesign DEC-FE-008 §3.7 + Council seat #1
     * (PA-CP-2 re-engagement, 2026-05-11): this also cascades the
     * file-explorer index mirror — fe_root + fe_file + fe_walk_run rows
     * for the seeded root[0] — so orphan index rows don't accumulate.
     *
     * Mirror cleanup is **best-effort**. Legacy app_settings is the
     * source of truth; a failed mirror cleanup is logged but not
     * propagated as an error (the user's primary action — clearing the
     * sync folder setting — succeeded).
     */
    async clearHandle() {
        const tenantId = _activateTenantScope();
        _getSyncFolderChannel(tenantId);
        try {
            await db.app_settings.delete('syncFolderHandle');
            if (!_isCurrentTenantScope(tenantId)) {
                throw new Error('The authenticated tenant changed while the sync folder was being removed.');
            }
            _cachedHandle = null;
            _permissionGranted = false;
            _broadcastCacheInvalidation('invalidate-handle', tenantId);
        } catch (err) {
            console.warn('[SyncFolder] Failed to clear handle:', err?.message);
            throw new Error('Could not remove the sync folder setting. Please try again.');
        }

        // Mirror cleanup — derived store, best-effort. Dynamic import
        // avoids creating a hard dependency cycle: sync-folder.js -> db.js
        // is fine; sync-folder.js -> file-explorer-index.js -> db.js is
        // also fine, but lazy-loading keeps the boot graph identical to
        // pre-Phase-A so existing callers see no behavioural change.
        //
        // PB-CP-1 (file-explorer-redesign 2026-05-11): unregister the
        // FileSystemObserver BEFORE removeRoot per Council seat #4
        // PB-CP-0 cascade-coordination finding. Order matters:
        // observer subscription is keyed off rootId; if removeRoot runs
        // first the unregister still works (idempotent), but doing it
        // in this order makes the intent obvious + avoids a transient
        // window where the observer fires for a deleted root.
        try {
            const { listRoots, removeRoot } = await import('./file-explorer-index');
            const { unregisterObserverForRoot } = await import('./file-explorer-observer');
            const roots = await listRoots();
            for (const root of roots) {
                try { await unregisterObserverForRoot(root.id); }
                catch (err) { console.warn('[SyncFolder] observer unregister threw:', err?.message); }
                await removeRoot(root.id);
            }
        } catch (err) {
            console.warn('[SyncFolder] file-explorer index mirror cleanup failed (best-effort):', err?.message);
            // Intentionally not rethrown — legacy clearHandle succeeded.
        }
    },

    /**
     * Check if a sync folder has been configured.
     */
    async isConfigured() {
        const handle = await this.getHandle();
        return !!handle;
    },

    /**
     * Get the folder name (for display in settings).
     */
    async getFolderName() {
        const handle = await this.getHandle();
        return handle?.name || null;
    },

    /**
     * Re-request permission on the stored handle. Ordinary callers are
     * read-only; a write-capable caller must explicitly pass
     * `{ mode: 'readwrite' }`.
     * Returns true if granted, false if denied or no handle.
     *
     * Browsers require `requestPermission()` to fire inside a user-gesture
     * event handler (click/keypress). When we're called from a background
     * timer / sync loop, that gesture doesn't exist and the call would throw
     * "User activation is required to request permissions": noisy in the
     * console even though the catch silently no-ops. Fix: query the current
     * permission state first. If already 'granted' we return true without
     * prompting. If 'prompt' (needs user interaction) we only call
     * requestPermission when this was invoked from a user-gesture context
     * (passUserGesture=true). Otherwise bail quietly: the next manual
     * "Connect folder" click in Settings will pop the browser's permission
     * dialog.
     *
     * @param {{passUserGesture?: boolean, mode?: 'read'|'readwrite'}} opts:
     *   set passUserGesture=true from a click handler so the request prompt
     *   can render. Mode defaults to read.
     */
    async ensurePermission(opts = {}) {
        const handle = await this.getHandle();
        if (!handle) return false;
        const permissionMode = opts.mode === 'readwrite' ? 'readwrite' : 'read';
        if (_permissionGranted) {
            try {
                if (typeof handle.queryPermission !== 'function') return true;
                const cachedState = await handle.queryPermission({ mode: permissionMode });
                if (cachedState === 'granted') return true;
                _permissionGranted = false;
                if (cachedState === 'denied') return false;
                // cachedState === 'prompt': fall through to the normal
                // user-gesture gate below.
            } catch {
                return true;
            }
        }

        // Tier 1: query current state without prompting.
        try {
            const state = await handle.queryPermission({ mode: permissionMode });
            if (state === 'granted') {
                _permissionGranted = true;
                return true;
            }
            if (state === 'denied') return false;
            // state === 'prompt': need a user gesture to proceed.
        } catch { /* ignore: fall through to Tier 2 */ }

        // Tier 2. Only attempt request when caller promises user gesture.
        if (!opts.passUserGesture) return false;

        try {
            const perm = await handle.requestPermission({ mode: permissionMode });
            _permissionGranted = perm === 'granted';
            return _permissionGranted;
        } catch (err) {
            console.warn('[SyncFolder] Permission request failed:', err?.message);
            return false;
        }
    },

    /**
     * Silently re-attach the previously-stored folder handle WITHOUT opening
     * the directory picker. Use when a project loads from history and recovery
     * silently bailed out because Chrome dropped the permission grant back to
     * 'prompt' between sessions — the handle is still in IndexedDB, we just
     * need a fresh permission acknowledgement.
     *
     * Must be invoked inside a user-gesture click handler — Chrome blocks
     * `requestPermission` outside one. Returns a discriminated result so
     * the caller can branch on the failure reason without parsing error
     * messages:
     *
     *   { ok: true,  handle }                            — permission granted
     *   { ok: false, reason: 'no-handle' }               — nothing stored to reconnect
     *   { ok: false, reason: 'denied' }                  — user rejected the prompt
     *   { ok: false, reason: 'unsupported' }             — old browser / no FSA
     *   { ok: false, reason: 'error', error }            — unexpected throw
     *
     * Does NOT call showDirectoryPicker. The persisted handle is reused
     * verbatim — folder name + on-disk location are remembered.
     *
     * Peter 2026-05-18 (FAD-project banner repro): "I want a button that
     * re-uses the same folder and connects it. I don't want it to open up
     * the file picker and have to pick a folder again."
     */
    async reconnectExistingHandle() {
        if (!this.isSupported()) {
            return { ok: false, reason: 'unsupported' };
        }
        const handle = await this.getHandle();
        if (!handle) {
            return { ok: false, reason: 'no-handle' };
        }
        try {
            const granted = await this.ensurePermission({ passUserGesture: true });
            if (granted) {
                return { ok: true, handle };
            }
            return { ok: false, reason: 'denied' };
        } catch (err) {
            console.warn('[SyncFolder] reconnectExistingHandle threw:', err?.message);
            return { ok: false, reason: 'error', error: err };
        }
    },

    /**
     * Open the user's folder picker and store the selected folder.
     * Returns { name, handle } or null if cancelled.
     */
    async pickFolder() {
        if (!this.isSupported()) throw new Error(this.getUnsupportedMessage('Folder sync'));
        try {
            // 2026-05-11: dropped `id: 'quotemaker-sync-root'` from the
            // options bag. The id parameter triggers Chromium's stable
            // "remember the last folder for this id" persistence path,
            // which requires permission grants the in-app browser
            // context refuses — observed in Peter's repro: Settings
            // throws NotAllowedError ("BLOCKED — Folder sync could not
            // open the local folder picker in this browser context")
            // while File Explorer's pickExplorerParentFolder uses the
            // same showDirectoryPicker without `id` and works fine.
            // Aligning the call signature with File Explorer makes
            // both surfaces behave identically across Chrome, Edge,
            // and the in-app browser. The "remember last folder" UX
            // was nice-to-have, not load-bearing — Chrome still
            // defaults to the previously-picked folder via the
            // session-default heuristic.
            const handle = await window.showDirectoryPicker({
                mode: 'read',
                startIn: 'documents',
            });
            if (!handle || handle.kind !== 'directory') {
                throw new Error('Chrome did not return a folder handle. Please choose a normal local, OneDrive, Dropbox, Google Drive, or network project folder.');
            }
            let permission = 'granted';
            try {
                if (typeof handle.queryPermission === 'function') {
                    permission = await handle.queryPermission({ mode: 'read' });
                }
                if (permission !== 'granted' && typeof handle.requestPermission === 'function') {
                    permission = await handle.requestPermission({ mode: 'read' });
                }
            } catch (permErr) {
                throw new Error(`Chrome could not grant read access to "${handle.name || 'that folder'}". Choose a normal work folder and allow access when prompted.`);
            }
            if (permission !== 'granted') {
                throw new Error(`QuoteMaker needs read permission for "${handle.name || 'that folder'}" so it can use the project reference files.`);
            }
            await this.setHandle(handle);
            return { name: handle.name, handle };
        } catch (err) {
            if (err.name === 'AbortError') return null; // User cancelled
            throw normaliseDirectoryPickerError(err, 'Folder sync');
        }
    },

    // getProjectSubfolder() removed - was dead code with a naming strategy
    // that competed with _buildFolderName(). All folder creation now goes
    // through syncProject() using _buildFolderName() for consistency.

    /**
     * Build a clean, descriptive folder name from project data.
     * Pattern: "2026-04-28 - QR 91787 - Strata Partners - SP 82539 - 67 69 Stanley Street Chatswood - P abc12345"
     */
    _buildFolderName(projectRecord) {
        return buildSyncFolderName(projectRecord);
    },

    _safeDirectoryName(name) {
        return String(name || 'QuoteMaker Project')
            .replace(/[<>:"/\\|?*\x00-\x1f]/g, ' ')
            .replace(/\s+/g, ' ')
            .trim()
            .replace(/[. ]+$/g, '')
            .slice(0, 170)
            || 'QuoteMaker Project';
    },

    _projectId(projectRecord = {}) {
        return String(projectRecord.id || projectRecord.dexieId || projectRecord.projectId || '').trim();
    },

    _sameProjectId(left, right) {
        return String(left || '').trim().toLowerCase() === String(right || '').trim().toLowerCase();
    },

    async _readSyncManifest(dirHandle) {
        try {
            const manifestHandle = await dirHandle.getFileHandle('SyncManifest.json');
            const manifestFile = await manifestHandle.getFile();
            return JSON.parse(await manifestFile.text());
        } catch {
            return null;
        }
    },

    _stripRootNameFromPath(rootHandle, path) {
        const normalised = normaliseSyncRelativePath(path);
        const rootName = String(rootHandle?.name || '').trim().toLowerCase();
        if (!normalised || !rootName) return normalised;
        const parts = normalised.split('/');
        if (String(parts[0] || '').trim().toLowerCase() === rootName) {
            return parts.slice(1).join('/');
        }
        return normalised;
    },

    _projectFolderHints(projectRecord = {}, desiredFolderName = '') {
        const folder = projectRecord.projectFolder || {};
        const paths = new Set();
        const names = new Set();

        const addPath = (value) => {
            const path = normaliseSyncRelativePath(value);
            if (path) paths.add(path);
        };
        const addName = (value) => {
            const safe = this._safeDirectoryName(value);
            if (safe) names.add(safe.toLowerCase());
            const raw = String(value || '').trim();
            if (raw) names.add(raw.toLowerCase());
        };

        addPath(folder.relativePath);
        addPath(folder.path);
        addPath(folder.sourcePath);
        addPath(folder.syncRelativePath);

        addName(desiredFolderName);
        addName(folder.subfolderName);
        addName(folder.folderName);
        addName(folder.name);
        addName(projectRecord.syncFolderName);
        addName(projectRecord.desiredSyncFolderName);

        for (const path of paths) {
            const parts = path.split('/').filter(Boolean);
            addName(parts[parts.length - 1]);
        }

        return { paths: [...paths], names };
    },

    async _getDirectoryByRelativePath(rootHandle, relativePath) {
        const path = normaliseSyncRelativePath(relativePath);
        if (!path) return rootHandle;
        let current = rootHandle;
        for (const segment of path.split('/')) {
            current = await current.getDirectoryHandle(segment, { create: false });
        }
        return current;
    },

    async _readQuoteData(dirHandle) {
        try {
            const qdHandle = await dirHandle.getFileHandle('QuoteData.json');
            const qdFile = await qdHandle.getFile();
            return JSON.parse(await qdFile.text());
        } catch {
            return null;
        }
    },

    async *_childDirectories(rootHandle) {
        if (!rootHandle) return;
        if (typeof rootHandle.values === 'function') {
            for await (const entry of rootHandle.values()) {
                if (entry.kind === 'directory') yield entry;
            }
            return;
        }
        if (typeof rootHandle.entries === 'function') {
            for await (const [, entry] of rootHandle.entries()) {
                if (entry.kind === 'directory') yield entry;
            }
        }
    },

    async _findExistingProjectDirDeep(rootHandle, projectRecord, desiredFolderName, options = {}) {
        const hints = this._projectFolderHints(projectRecord, desiredFolderName);
        const projectId = this._projectId(projectRecord);
        const safeName = this._safeDirectoryName(desiredFolderName);

        for (const hintedPath of hints.paths) {
            const path = this._stripRootNameFromPath(rootHandle, hintedPath);
            try {
                const exact = await this._getDirectoryByRelativePath(rootHandle, path);
                return {
                    projectDir: exact,
                    folderName: exact?.name || safeName,
                    desiredFolderName: safeName,
                    relativePath: path,
                };
            } catch {
                // The saved customer path may have moved. Fall through to
                // manifest/name discovery below.
            }
        }

        const maxDepth = options.maxDepth ?? MAX_PROJECT_DIRECTORY_SCAN_DEPTH;
        const maxDirectories = options.maxDirectories ?? MAX_PROJECT_DIRECTORY_SCAN_COUNT;
        let visited = 0;

        const matchesDirectory = async (dirHandle, relativePath) => {
            const dirName = String(dirHandle?.name || '').trim().toLowerCase();
            if (hints.names.has(dirName)) {
                return true;
            }

            const manifest = await this._readSyncManifest(dirHandle);
            if (manifest && projectId && this._sameProjectId(manifest.projectId, projectId)) {
                return true;
            }

            const quoteData = await this._readQuoteData(dirHandle);
            if (quoteData && projectId && this._sameProjectId(quoteData.projectId, projectId)) {
                return true;
            }

            const leaf = normaliseSyncRelativePath(relativePath).split('/').filter(Boolean).pop();
            return leaf && hints.names.has(String(leaf).toLowerCase());
        };

        const walk = async (dirHandle, relativePath = '', depth = 0) => {
            if (!dirHandle || visited >= maxDirectories) return null;
            visited += 1;

            if (depth > 0 && await matchesDirectory(dirHandle, relativePath)) {
                return {
                    projectDir: dirHandle,
                    folderName: dirHandle.name || safeName,
                    desiredFolderName: safeName,
                    relativePath,
                };
            }

            if (depth >= maxDepth) return null;

            for await (const entry of this._childDirectories(dirHandle)) {
                if (visited >= maxDirectories) break;
                if (!entry || entry.kind !== 'directory') continue;
                if (SYNC_SCAN_IGNORED_DIRECTORY_NAMES.has(String(entry.name || '').toLowerCase())) continue;
                const found = await walk(entry, joinSyncRelativePath(relativePath, entry.name), depth + 1);
                if (found) return found;
            }
            return null;
        };

        return walk(rootHandle);
    },

    async _findExistingProjectDir(rootHandle, projectRecord, desiredFolderName) {
        const safeName = this._safeDirectoryName(desiredFolderName);
        const hints = this._projectFolderHints(projectRecord, desiredFolderName);
        if (hints.names.has(String(rootHandle?.name || '').trim().toLowerCase())) {
            return { projectDir: rootHandle, folderName: rootHandle.name || safeName, desiredFolderName: safeName, relativePath: '' };
        }
        try {
            const direct = await rootHandle.getDirectoryHandle(safeName);
            return { projectDir: direct, folderName: direct.name || safeName, desiredFolderName: safeName };
        } catch {
            // No direct match. Fall through to manifest lookup for renamed folders.
        }

        for (const hintedPath of hints.paths) {
            const path = this._stripRootNameFromPath(rootHandle, hintedPath);
            try {
                const exact = await this._getDirectoryByRelativePath(rootHandle, path);
                return { projectDir: exact, folderName: exact.name || safeName, desiredFolderName: safeName, relativePath: path };
            } catch {
                // Saved folder path may have moved or been renamed.
            }
        }

        const projectId = this._projectId(projectRecord);

        try {
            for await (const entry of this._childDirectories(rootHandle)) {
                if (hints.names.has(String(entry?.name || '').trim().toLowerCase())) {
                    return { projectDir: entry, folderName: entry.name || safeName, desiredFolderName: safeName, relativePath: entry.name || '' };
                }
                const manifest = await this._readSyncManifest(entry);
                if (manifest && projectId && this._sameProjectId(manifest.projectId, projectId)) {
                    return { projectDir: entry, folderName: entry.name || manifest.folderName || safeName, desiredFolderName: safeName };
                }
            }
            return this._findExistingProjectDirDeep(rootHandle, projectRecord, desiredFolderName);
        } catch (err) {
            if (!isTransientFileSystemError(err)) throw err;
            console.warn('[SyncFolder] Existing project folder scan skipped because the local sync folder changed mid-read:', err?.message);
            return null;
        }
    },

    // v5.19.22 — Read A (Peter 2026-05-05): sync no longer creates ANY folder.
    // Metafiles (SyncManifest.json + photos/ + documents/ + videos/ + emails/
    // + Project_Index.md) are written INTO the project's existing on-disk
    // folder. If the project has no folder on disk yet, sync stays inert and
    // the caller (syncProject) returns a graceful skip with reason
    // 'no_on_disk_folder' so the user is nudged to import via FileExplorer.
    //
    // The legacy v5.19.11 "QuoteMaker Projects/" wrapper container is kept as
    // a READ-ONLY backwards-compat path so any folders previously created
    // there continue to be found and synced; we never write into the wrapper
    // for new projects.
    async _resolveProjectsContainer(rootHandle) {
        if (!rootHandle) return null;
        try {
            // Read-only — never pass { create: true }. v5.19.22 (Read A) removed
            // the create branch entirely. If the wrapper doesn't exist, return
            // null and the caller falls through to root-level lookup.
            return await rootHandle.getDirectoryHandle(PROJECTS_SUBFOLDER_NAME, { create: false });
        } catch {
            return null;
        }
    },

    async _getProjectDirectory(rootHandle, projectRecord, { create = false } = {}) {
        const desiredFolderName = this._safeDirectoryName(this._buildFolderName(projectRecord));
        let lastError = null;
        const maxAttempts = 6;
        for (let attempt = 0; attempt < maxAttempts; attempt++) {
            try {
                // 1) Backwards-compat lookup inside the legacy v5.19.11
                //    "QuoteMaker Projects/" wrapper. Read-only; we never
                //    create or write a new folder there.
                const container = await this._resolveProjectsContainer(rootHandle);
                if (container) {
                    const inContainer = await this._findExistingProjectDir(container, projectRecord, desiredFolderName);
                    if (inContainer) return inContainer;
                }
                // 2) Standard lookup at the sync root level.
                const inRoot = await this._findExistingProjectDir(rootHandle, projectRecord, desiredFolderName);
                if (inRoot) return inRoot;
                // 3) Read A: no on-disk folder for this project. Return null
                //    regardless of `create` flag — sync no longer creates
                //    folders. Caller checks for null and skips gracefully.
                return null;
            } catch (err) {
                lastError = err;
                if (!isTransientFileSystemError(err) || attempt === maxAttempts - 1) throw err;
                await refreshDirectoryHandleState(rootHandle);
                await delay(projectDirectoryRetryDelay(attempt));
            }
        }
        throw lastError || new Error('Project sync folder could not be opened.');
    },

    /**
     * Build a clean photo filename from metadata.
     * Pattern: "001_Roof_damage_north_elevation.jpg"
     */
    _buildPhotoName(photo, index) {
        const num = String(index + 1).padStart(3, '0');
        // Use AI label if available, then name, then generic
        const label = (photo.aiLabel || photo.label || photo.name || photo.originalName || 'photo')
            .replace(/\.[^.]+$/, '')  // Remove extension
            .replace(/[<>:"/\\|?*]/g, '_')
            .replace(/\s+/g, '_')
            .substring(0, 60);
        const ext = (photo.mimeType || 'image/jpeg').includes('png') ? '.png' : '.jpg';
        return `${num}_${label}${ext}`;
    },

    /**
     * Build a clean document filename.
     * Pattern: "RFQ_Scope_of_Works.pdf" or "Asbestos_Report_extracted.txt"
     */
    _buildDocName(doc, index, suffix = '') {
        const rawName = doc.name || `Document_${index + 1}`;
        const baseName = rawName
            .replace(/\.[^.]+$/, '')  // Remove extension
            .replace(/[<>:"/\\|?*]/g, '_')
            .replace(/\s+/g, '_')
            .substring(0, 60);
        return `${baseName}${suffix}`;
    },

    /**
     * Sync a project's files to the sync folder.
     * Non-blocking, fire-and-forget - errors are logged, never thrown.
     *
     * @param {Object} projectRecord - the full project record from saveCurrentProject
     * @param {Array}  photos        - current photos array (with blobId/data refs)
     * @param {Array}  documents     - current documents array
     * @param {Array}  videos        - current videos array (may have .file on first session)
     * @param {Array}  learnings     - AI learnings relevant to this project (optional)
     */
    async syncProject(projectRecord, photos, documents, videos, learnings) {
        // Folder-dumping disabled by default (operator request 2026-07-09).
        // QuoteMaker no longer auto-writes Inspector_Notes.txt / Risk_Report.txt /
        // Project_Index.md / AI_Learnings.json / QuoteData.json / SyncManifest.json
        // into the bound project folder on every save. Reference files + photos
        // persist in the app's OWN durable storage (document/photo blob stores +
        // S3), keyed to the project, so a completed project's folder can be moved
        // out of the synced folder without losing anything. The only file
        // QuoteMaker writes into the folder is the final quote PDF, on an explicit
        // Save/Download (a separate path — saveBlobToQuoteOutput). Set localStorage
        // qm_folder_sync_writes='true' to restore the old folder-archive behaviour.
        try {
            if (typeof localStorage === 'undefined' || localStorage.getItem('qm_folder_sync_writes') !== 'true') {
                return {
                    success: true,
                    skipped: true,
                    reason: 'folder_sync_writes_disabled',
                    results: { photos: 0, documents: 0, docTexts: 0, videos: 0, learnings: false, quote: false, notes: false, riskReport: false, projectIndex: false },
                    photoSyncFileNames: [],
                    documentSyncFileNames: [],
                    videoSyncFileNames: [],
                };
            }
        } catch {
            // Fail closed. A browser storage exception must never resurrect the
            // retired background folder writer.
            return {
                success: true,
                skipped: true,
                reason: 'folder_sync_writes_disabled',
                results: { photos: 0, documents: 0, docTexts: 0, videos: 0, learnings: false, quote: false, notes: false, riskReport: false, projectIndex: false },
                photoSyncFileNames: [],
                documentSyncFileNames: [],
                videoSyncFileNames: [],
            };
        }

        if (!await this.ensurePermission({ mode: 'readwrite' })) return;

        const handle = await this.getHandle();
        if (!handle) return;

        let projectDir;
        let folderName;
        let desiredFolderName;
        try {
            const dirInfo = await this._getProjectDirectory(handle, projectRecord, { create: true });
            // v5.19.22 (Read A, Peter 2026-05-05): sync no longer creates folders.
            // Null means the project has no on-disk folder yet. Skip gracefully —
            // the user must import the folder via FileExplorer to enable sync.
            if (!dirInfo) {
                console.info('[SyncFolder] Project has no on-disk folder; sync skipped. Import via File Explorer to enable sync.');
                return {
                    success: false,
                    skipped: true,
                    reason: 'no_on_disk_folder',
                    results: { photos: 0, documents: 0, docTexts: 0, videos: 0, learnings: false, quote: false, notes: false, riskReport: false, projectIndex: false },
                    photoSyncFileNames: [],
                    documentSyncFileNames: [],
                    videoSyncFileNames: [],
                };
            }
            projectDir = dirInfo.projectDir;
            folderName = dirInfo.folderName;
            desiredFolderName = dirInfo.desiredFolderName;
        } catch (err) {
            const transient = isTransientFileSystemError(err);
            const message = transient
                ? 'Local sync deferred because Chrome reported the selected folder is still changing. The QuoteMaker project save is intact.'
                : 'Failed to access project folder for sync.';
            (transient ? console.warn : console.error)(`[SyncFolder] ${message}`, err?.message);
            return {
                success: false,
                transient,
                error: err?.message || String(err),
                results: { photos: 0, documents: 0, docTexts: 0, videos: 0, learnings: false, quote: false, notes: false, riskReport: false, projectIndex: false },
                photoSyncFileNames: [],
                documentSyncFileNames: [],
                videoSyncFileNames: [],
            };
        }

        const results = { photos: 0, documents: 0, docTexts: 0, videos: 0, learnings: false, quote: false, notes: false, riskReport: false, projectIndex: false };
        const photoSyncFileNames = [];
        const documentSyncFileNames = [];
        const videoSyncFileNames = [];
        const photoSyncFailures = [];
        const documentSyncFailures = [];
        const videoSyncFailures = [];
        const dataSyncFailures = [];

        // ── Photos / Documents / Videos (DE-BLOAT, v5.37.9 — manifest v5) ──
        // The originals already live where the user put them (intake folder /
        // FileExplorer source path); QM no longer duplicates them into the
        // project folder. The `_extracted.txt` sidecars are likewise redundant
        // — QuoteData.json carries documents[i].content (extracted text)
        // verbatim, which is what every downstream consumer reads
        // (document-intelligence.js, quote-generation.js, rfq-mode-classifier.js,
        // preprocessing-pipeline.js, etc.; all access `doc.content`).
        //
        // Result counters + syncFileName arrays remain initialised so the
        // manifest writer + sync-result envelope shape is unchanged for any
        // consumer that reads them. They simply stay at 0 / [].
        //
        // Non-destructive: existing photos/ + documents/ + videos/ subfolders
        // on Rodney's v5.37.4 box (manifest v4) are NOT cleaned up here. A
        // separate opt-in "Tidy sync folders" action can land later. Restore
        // path is back-compat with v4 — see restoreProjectFromFolder below.

        // ── AI Learnings (now at project root — manifest v5) ──
        try {
            if (learnings && learnings.length > 0) {
                const learningsData = learnings.map(l => ({
                    type: l.type,
                    category: l.category,
                    original: l.original,
                    corrected: l.corrected,
                    context: l.context,
                    confidence: l.confidence,
                    useCount: l.useCount,
                    createdAt: l.createdAt,
                    lastUsedAt: l.lastUsedAt,
                }));
                const learningsBlob = new Blob([JSON.stringify({
                    projectId: projectRecord.id || projectRecord.dexieId,
                    exportedAt: new Date().toISOString(),
                    count: learningsData.length,
                    learnings: learningsData,
                }, null, 2)], { type: 'application/json' });
                await writeSyncFileWithRetry(projectDir, 'AI_Learnings.json', learningsBlob, {
                    parentHandle: projectDir,
                });
                results.learnings = true;
            }
        } catch (err) {
            dataSyncFailures.push(makeSyncFailure({ name: 'AI_Learnings.json', fileName: 'AI_Learnings.json', phase: 'learnings', err }));
            logSyncFailure('[SyncFolder] Learnings sync failed:', err);
        }

        // ── Inspector / Site Notes ──
        try {
            const notes = projectRecord.formData?.notes;
            if (notes && typeof notes === 'string' && notes.trim().length > 0) {
                const fd = projectRecord.formData || {};
                const header = [
                    `Site: ${fd.address || 'Unknown'}`,
                    fd.clientCompany ? `Client: ${fd.clientCompany}` : null,
                    fd.clientName ? `Contact: ${fd.clientName}` : null,
                    fd.strataplan ? `Strata Plan: ${fd.strataplan}` : null,
                    `Exported: ${new Date().toISOString()}`,
                    '='.repeat(50),
                    '',
                ].filter(Boolean).join('\n');
                const notesBlob = new Blob([header + notes], { type: 'text/plain' });
                await writeSyncFileWithRetry(projectDir, 'Inspector_Notes.txt', notesBlob, {
                    parentHandle: projectDir,
                });
                results.notes = true;
            }
        } catch (err) {
            dataSyncFailures.push(makeSyncFailure({ name: 'Inspector_Notes.txt', fileName: 'Inspector_Notes.txt', phase: 'notes', err }));
            logSyncFailure('[SyncFolder] Notes sync failed:', err);
        }

        // ── Quote Data JSON (always overwrite - latest snapshot) ──
        try {
            const title = projectRecord.title || projectRecord.formData?.address || 'Untitled';
            const quoteBlob = new Blob([JSON.stringify({
                syncSchemaVersion: 3,
                projectId: projectRecord.id || projectRecord.dexieId,
                syncFolderName: folderName,
                desiredSyncFolderName: desiredFolderName,
                title,
                quoteNumber: projectRecord.formData?.orderNumber || projectRecord.quoteNumber,
                status: projectRecord.status,
                currentStage: projectRecord.currentStage,
                completedStages: projectRecord.completedStages,
                formData: projectRecord.formData,
                quoteData: projectRecord.quoteData,
                scope: projectRecord.scope,
                riskAnalysis: projectRecord.riskAnalysis ? {
                    confidence_score: projectRecord.riskAnalysis.confidence_score,
                    detected_risks: projectRecord.riskAnalysis.detected_risks,
                    executive_summary: projectRecord.riskAnalysis.executive_summary,
                } : null,
                photoCount: (photos || []).length,
                documentCount: (documents || []).length,
                savedAt: new Date().toISOString(),
            }, null, 2)], { type: 'application/json' });
            await writeSyncFileWithRetry(projectDir, 'QuoteData.json', quoteBlob, {
                parentHandle: projectDir,
            });
            results.quote = true;
        } catch (err) {
            dataSyncFailures.push(makeSyncFailure({ name: 'QuoteData.json', fileName: 'QuoteData.json', phase: 'quote_data', err }));
            logSyncFailure('[SyncFolder] QuoteData.json sync failed:', err);
        }

        // ── Risk Report (human-readable) ──
        try {
            const ra = projectRecord.riskAnalysis;
            if (ra && (ra.executive_summary || ra.detected_risks?.length)) {
                const lines = [
                    `BUILDING RISK ASSESSMENT REPORT`,
                    `Site: ${projectRecord.formData?.address || 'Unknown'}`,
                    `Generated: ${ra.analysisTimestamp || new Date().toISOString()}`,
                    `Confidence: ${ra.confidence_score || 'N/A'}%`,
                    '='.repeat(60),
                    '',
                ];
                if (ra.executive_summary) {
                    lines.push('EXECUTIVE SUMMARY', '-'.repeat(40), ra.executive_summary, '');
                }
                if (ra.detected_risks?.length) {
                    lines.push('DETECTED RISKS', '-'.repeat(40));
                    ra.detected_risks.forEach((r, i) => {
                        lines.push(`${i + 1}. [${(r.severity || 'unknown').toUpperCase()}] ${r.title || r.description || 'Unnamed risk'}`);
                        if (r.description && r.description !== r.title) lines.push(`   ${r.description}`);
                    });
                    lines.push('');
                }
                const reportBlob = new Blob([lines.join('\n')], { type: 'text/plain' });
                await writeSyncFileWithRetry(projectDir, 'Risk_Report.txt', reportBlob, {
                    parentHandle: projectDir,
                });
                results.riskReport = true;
            }
        } catch (err) {
            dataSyncFailures.push(makeSyncFailure({ name: 'Risk_Report.txt', fileName: 'Risk_Report.txt', phase: 'risk_report', err }));
            logSyncFailure('[SyncFolder] Risk report sync failed:', err);
        }

        // ── Human-readable corpus index ──
        try {
            const indexMarkdown = buildProjectIndexMarkdown({
                projectRecord,
                folderName,
                photos,
                documents,
                videos,
                results,
            });
            const indexBlob = new Blob([indexMarkdown], { type: 'text/markdown' });
            await writeSyncFileWithRetry(projectDir, 'Project_Index.md', indexBlob, {
                parentHandle: projectDir,
            });
            results.projectIndex = true;
        } catch (err) {
            dataSyncFailures.push(makeSyncFailure({ name: 'Project_Index.md', fileName: 'Project_Index.md', phase: 'project_index', err }));
            logSyncFailure('[SyncFolder] Project_Index.md sync failed:', err);
        }

        // ── SyncManifest.json (file inventory for restore) ──
        // ── v4 schema (BQ-CP-2 / DEC-BQ-002 / DEC-BQ-012, 2026-05-03) ──
        // Additive bump from v3:
        //   - keyFields.{companyId, propertyId, contactId} — canonical CRM IDs
        //     alongside the existing text fields. Lets the file explorer pivot
        //     into the CRM Workbench Sites/Requesters mode for a folder.
        //     Coordinated with the CRM Workbench Redesign team's canonical-
        //     terminology pass (see docs/crm-workbench-redesign/).
        //   - documents[].{class, confidence, evidence, classifiedAt} —
        //     persists `document-type-classifier.js` output so unsynced-folder
        //     scan + content-aware buildSignals don't have to re-classify
        //     every PDF on every folder open.
        //   - keyFields.status now permits 'ready_for_builder_review' (DEC-BQ-003)
        //     emitted by Phase 2's batch processor when Stage 5 completes.
        //
        // Backward-compat (verified by schema-drift-detector audit, INVESTIGATION-
        // REPORT.md §2): every existing reader handles missing v4 fields with
        // null/empty defaults. v3 manifests on disk read cleanly. v4 manifests
        // read by stale v3 code degrade gracefully (extra fields ignored,
        // unknown status falls through STATUS_ALIASES to 'draft').
        // DEC-COUNCIL-007 / Candidate C5 Slice 1: manifest shape lives in
        // sync-folder-manifest.js. The orchestrator owns I/O + helper binding;
        // the module owns the v4 schema, dataFiles registry, identifier
        // extraction. See docs/council/DECISION-LOG.md DEC-COUNCIL-007.
        try {
            const manifest = buildSyncManifestV4({
                projectRecord,
                folderName,
                desiredFolderName,
                photos: photos || [],
                documents: documents || [],
                videos: videos || [],
                documentSyncFileNames,
                videoSyncFileNames,
                results,
                photoSyncFailures,
                documentSyncFailures,
                videoSyncFailures,
                dataSyncFailures,
                helpers: {
                    buildPhotoName: this._buildPhotoName.bind(this),
                    buildDocName: this._buildDocName.bind(this),
                    isPdfDocument,
                    buildVideoStoragePolicy,
                    VIDEO_ORIGINAL_STORAGE,
                },
            });
            const manifestBlob = new Blob([JSON.stringify(manifest, null, 2)], { type: 'application/json' });
            await writeSyncFileWithRetry(projectDir, 'SyncManifest.json', manifestBlob, {
                parentHandle: projectDir,
            });
        } catch (err) {
            dataSyncFailures.push(makeSyncFailure({ name: 'SyncManifest.json', fileName: 'SyncManifest.json', phase: 'sync_manifest', err }));
            logSyncFailure('[SyncFolder] SyncManifest.json sync failed:', err);
        }

        // ── Persist sync result for UI display ──
        const hasVideoSyncFailures = videoSyncFailures.length > 0;
        const hasPhotoSyncFailures = photoSyncFailures.length > 0;
        const hasDocumentSyncFailures = documentSyncFailures.length > 0;
        const hasDataSyncFailures = dataSyncFailures.length > 0;
        const hasAnySyncFailures = hasPhotoSyncFailures || hasDocumentSyncFailures || hasVideoSyncFailures || hasDataSyncFailures;
        const syncErrorMessage = buildSyncErrorMessage({ photoSyncFailures, documentSyncFailures, videoSyncFailures, dataSyncFailures });
        const needsReconnect = [...photoSyncFailures, ...documentSyncFailures, ...videoSyncFailures, ...dataSyncFailures]
            .some(f => f?.transient);
        const syncResult = {
            projectId: projectRecord.id || projectRecord.dexieId,
            projectTitle: projectRecord.title || projectRecord.formData?.address || 'Untitled',
            folderName,
            syncedAt: new Date().toISOString(),
            results,
            success: !hasAnySyncFailures,
            partial: hasAnySyncFailures,
            error: syncErrorMessage,
            needsReconnect,
            photoSyncFailures,
            documentSyncFailures,
            videoSyncFailures,
            dataSyncFailures,
        };
        try {
            await db.app_settings.put({ key: 'lastSyncResult', value: syncResult });
        } catch { /* non-critical */ }

        if (hasAnySyncFailures) {
            const allFailures = [...photoSyncFailures, ...documentSyncFailures, ...videoSyncFailures, ...dataSyncFailures];
            const partialPayload = {
                results,
                photoSyncFailures,
                documentSyncFailures,
                videoSyncFailures,
                dataSyncFailures,
            };
            if (allFailures.every(f => f?.transient)) {
                console.info(`[SyncFolder] Partially synced "${folderName}":`, partialPayload);
            } else {
                console.warn(`[SyncFolder] Partially synced "${folderName}":`, partialPayload);
            }
        } else {
            console.log(`[SyncFolder] Synced "${folderName}":`, results);
        }
        return { ...syncResult, videoSyncFileNames, photoSyncFileNames, documentSyncFileNames };
    },

    // ─── Sync Status ────────────────────────────────────────────────────────

    /**
     * Get the last sync result (for UI display).
     */
    async getLastSyncResult() {
        try {
            const record = await db.app_settings.get('lastSyncResult');
            return record?.value || null;
        } catch { return null; }
    },

    // ─── Video Read-Back ────────────────────────────────────────────────────

    /**
     * Load a video file from the sync folder for playback.
     * Returns an Object URL or null if unavailable.
     *
     * @param {Object} projectRecord - needed to find the project subfolder
     * @param {string} syncFileName - the video filename in videos/
     * @returns {Promise<string|null>} Object URL for video playback
     */
    async loadVideoFromSyncFolder(projectRecord, syncFileName) {
        if (!syncFileName) return null;
        try {
            if (!await this.ensurePermission()) return null;
            const handle = await this.getHandle();
            if (!handle) return null;

            const dirInfo = await this._getProjectDirectory(handle, projectRecord, { create: false });
            const projectDir = dirInfo?.projectDir || null;

            // 1) Legacy v4 layout: the original was duplicated into videos/.
            if (projectDir) {
                try {
                    const videosDir = await projectDir.getDirectoryHandle('videos');
                    const fileHandle = await videosDir.getFileHandle(syncFileName);
                    const file = await fileHandle.getFile();
                    return URL.createObjectURL(file);
                } catch {
                    // No videos/ subfolder (post v5.37.9 de-bloat) or file gone —
                    // fall through to the import-source search below.
                }
            }

            // 2) FE-MOVED-1: search the configured sync root's subtree for the
            //    original by filename. Sync stopped duplicating originals into
            //    videos/, so this is now the canonical recovery path.
            const file = await this._findVideoFileInTree(handle, { syncFileName });
            return file ? URL.createObjectURL(file) : null;
        } catch {
            return null; // Folder/file missing or permission denied - silent
        }
    },

    /**
     * FE-MOVED-1 (Peter 2026-06-13): re-acquire a video's original bytes from
     * its IMPORT SOURCE rather than the project's videos/ subfolder (which sync
     * stopped writing in the v5.37.9 de-bloat). Best-effort, never throws —
     * returns a File on success or null so callers degrade to the banner.
     *
     * Strategy, cheapest first:
     *   A. If the video carries a durable import-source relative path
     *      (quoteMakerSourcePath / relativePath / webkitRelativePath /
     *      localSourcePath — attached by file-explorer-intake at import time),
     *      try to resolve that exact path under the sync root (and, if a
     *      projectDir is known, relative to it too).
     *   B. Fall back to a bounded subtree search of the sync root for the
     *      video's filename (syncFileName / name). This is what fires for
     *      plain file-picker imports that carry only a filename, and for any
     *      case where the source path no longer resolves because the folder
     *      moved.
     *
     * @param {FileSystemDirectoryHandle} rootHandle - the configured sync root
     * @param {Object} video - the video object (or a {syncFileName} shape)
     * @param {FileSystemDirectoryHandle|null} projectDir - resolved project dir, if any
     * @returns {Promise<File|null>}
     */
    async _recoverVideoFromSource(rootHandle, video, projectDir = null) {
        if (!rootHandle || !video) return null;

        // A) Try the recorded import-source relative path(s).
        const candidatePaths = [];
        const addPath = (value) => {
            const path = normaliseSyncRelativePath(value);
            if (path && !candidatePaths.includes(path)) candidatePaths.push(path);
        };
        addPath(video.quoteMakerSourcePath);
        addPath(video.relativePath);
        addPath(video.webkitRelativePath);
        addPath(video.localSourcePath);

        for (const candidate of candidatePaths) {
            // Resolve against the sync root, tolerating a leading root-name
            // segment the same way _findExistingProjectDirDeep does.
            const stripped = this._stripRootNameFromPath(rootHandle, candidate);
            const file = await this._getFileByRelativePath(rootHandle, stripped);
            if (file) return file;
            // Also try relative to the located project directory, in case the
            // recorded path was scoped to the project folder rather than root.
            if (projectDir) {
                const fromProject = await this._getFileByRelativePath(projectDir, candidate);
                if (fromProject) return fromProject;
            }
        }

        // B) Filename subtree fallback.
        const fileNames = [];
        const addName = (value) => {
            const name = String(value || '').trim();
            if (name && !fileNames.includes(name)) fileNames.push(name);
        };
        addName(video.syncFileName);
        addName(video.name);
        addName(video.originalName);
        // The recorded relative paths may carry a different (un-sanitised) leaf
        // name than syncFileName — search for those leaves too.
        for (const candidate of candidatePaths) {
            const leaf = candidate.split('/').filter(Boolean).pop();
            addName(leaf);
        }
        if (fileNames.length === 0) return null;

        return this._findVideoFileInTree(rootHandle, { fileNames });
    },

    /**
     * Resolve a file by relative path under a directory handle. Returns the
     * File on success or null (never throws) so callers can chain candidates.
     */
    async _getFileByRelativePath(dirHandle, relativePath) {
        const path = normaliseSyncRelativePath(relativePath);
        if (!dirHandle || !path) return null;
        const parts = path.split('/').filter(Boolean);
        if (parts.length === 0) return null;
        try {
            let current = dirHandle;
            for (let i = 0; i < parts.length - 1; i++) {
                current = await current.getDirectoryHandle(parts[i], { create: false });
            }
            const fileHandle = await current.getFileHandle(parts[parts.length - 1]);
            return await fileHandle.getFile();
        } catch {
            return null;
        }
    },

    /**
     * Bounded breadth-first-ish subtree search for a file matching any of the
     * given names (case-insensitive). Honours the same ignored-directory set as
     * the project-folder scan and caps depth + visited-directory count so a
     * large cloud-synced tree can't stall recovery. Returns the first matching
     * File or null. Never throws.
     *
     * @param {FileSystemDirectoryHandle} rootHandle
     * @param {{ fileNames?: string[], syncFileName?: string }} opts
     */
    async _findVideoFileInTree(rootHandle, opts = {}) {
        if (!rootHandle) return null;
        const names = new Set(
            [...(opts.fileNames || []), opts.syncFileName]
                .map(n => String(n || '').trim().toLowerCase())
                .filter(Boolean),
        );
        if (names.size === 0) return null;

        let visited = 0;

        const walk = async (dirHandle, depth) => {
            if (!dirHandle || visited >= MAX_VIDEO_SOURCE_SCAN_COUNT) return null;
            visited += 1;

            // Look for a matching file in THIS directory first.
            try {
                if (typeof dirHandle.values === 'function') {
                    for await (const entry of dirHandle.values()) {
                        if (entry.kind === 'file' && names.has(String(entry.name || '').trim().toLowerCase())) {
                            try { return await entry.getFile(); } catch { /* unreadable — keep scanning */ }
                        }
                    }
                } else if (typeof dirHandle.entries === 'function') {
                    for await (const [, entry] of dirHandle.entries()) {
                        if (entry.kind === 'file' && names.has(String(entry.name || '').trim().toLowerCase())) {
                            try { return await entry.getFile(); } catch { /* unreadable — keep scanning */ }
                        }
                    }
                }
            } catch {
                // Directory iteration failed (permission/transient) — skip it.
                return null;
            }

            if (depth >= MAX_VIDEO_SOURCE_SCAN_DEPTH) return null;

            for await (const child of this._childDirectories(dirHandle)) {
                if (visited >= MAX_VIDEO_SOURCE_SCAN_COUNT) break;
                if (!child || child.kind !== 'directory') continue;
                if (SYNC_SCAN_IGNORED_DIRECTORY_NAMES.has(String(child.name || '').toLowerCase())) continue;
                const found = await walk(child, depth + 1);
                if (found) return found;
            }
            return null;
        };

        try {
            return await walk(rootHandle, 0);
        } catch {
            return null;
        }
    },

    // ─── Auto-Recovery ──────────────────────────────────────────────────────

    /**
     * Attempt to recover missing files from the sync folder.
     * Called during project load when photos have _dataLost or videos lack URLs.
     *
     * @param {Object} projectRecord
     * @param {Array} photos - with potential _dataLost flags
     * @param {Array} documents
     * @param {Array} videos
     * @returns {Promise<{photos: Array, documents: Array, videos: Array}>}
     */
    async recoverMissingFiles(projectRecord, photos, documents, videos) {
        const recovered = { photos: [], documents: [], videos: [] };
        try {
            if (!await this.ensurePermission()) return recovered;
            const handle = await this.getHandle();
            if (!handle) return recovered;

            const dirInfo = await this._getProjectDirectory(handle, projectRecord, { create: false });
            const projectDir = dirInfo?.projectDir || null;
            // FE-MOVED-1: photos + PDFs live under the project folder, so they
            // can only recover when a project dir was located. Videos are
            // import-source references (de-bloat), recoverable by filename from
            // the sync root even when the project folder itself can't be found
            // (e.g. folder moved + renamed) — so we don't early-return here.
            const manifest = projectDir ? await this._readSyncManifest(projectDir) : null;

            // Recover photos - use syncFileName (stable) first, fall back to derived name
            try {
                if (!projectDir) throw new Error('no project dir');
                const photosDir = await projectDir.getDirectoryHandle('photos');
                for (let i = 0; i < (photos || []).length; i++) {
                    const photo = photos[i];
                    // PDF XObjects/page renders are retained audit rows for
                    // their source document. They never become project-photo
                    // recovery work, even when a video or genuine photo causes
                    // this mixed-media recovery pass to run.
                    if (isDocumentDerivedVisual(photo)) continue;
                    if (!photo._dataLost && photo.blobId) continue; // Photo is fine
                    try {
                        // Prefer stored syncFileName (stable across AI relabeling)
                        const fileName = photo.syncFileName || this._buildPhotoName(photo, i);
                        const fileHandle = await photosDir.getFileHandle(fileName);
                        const file = await fileHandle.getFile();
                        recovered.photos.push({ index: i, blob: file, meta: photo });
                    } catch {
                        // If syncFileName failed, try derived name as last resort
                        if (photo.syncFileName) {
                            try {
                                const fallback = this._buildPhotoName(photo, i);
                                const fh = await photosDir.getFileHandle(fallback);
                                const f = await fh.getFile();
                                recovered.photos.push({ index: i, blob: f, meta: photo });
                            } catch { /* Both names failed - truly unrecoverable */ }
                        }
                    }
                }
            } catch { /* photos/ subfolder missing */ }

            // Recover PDFs - if the backend/file_metadata path is unavailable
            // or a legacy history row only has extracted text, a local-sync PDF
            // should still restore the actual previewable document.
            try {
                if (!projectDir) throw new Error('no project dir');
                const docsDir = await projectDir.getDirectoryHandle('documents');
                const manifestDocs = Array.isArray(manifest?.documents) ? manifest.documents : [];
                for (let i = 0; i < (documents || []).length; i++) {
                    const doc = documents[i];
                    if (!isPdfDocument(doc)) continue;
                    if (typeof doc?.pdfDataUrl === 'string' && (doc.pdfDataUrl.startsWith('blob:') || doc.pdfDataUrl.startsWith('data:application/pdf'))) continue;

                    const manifestEntry = manifestDocs.find(d => Number(d.index) === i)
                        || manifestDocs.find(d => d.originalName && d.originalName === (doc.name || doc.originalName));
                    const fileName = doc.syncFileName
                        || doc.localSyncFileName
                        || manifestEntry?.fileName
                        || this._buildDocName(doc, i) + '.pdf';
                    if (!fileName) continue;

                    try {
                        const fileHandle = await docsDir.getFileHandle(fileName);
                        const file = await fileHandle.getFile();
                        const pdfDataUrl = createPdfObjectUrl(file);
                        if (pdfDataUrl) {
                            recovered.documents.push({
                                index: i,
                                pdfBlob: normalizePdfBlob(file),
                                pdfDataUrl,
                                syncFileName: fileName,
                            });
                        }
                    } catch {
                        // File not found - try derived legacy name if manifest name failed.
                        if (fileName !== this._buildDocName(doc, i) + '.pdf') {
                            try {
                                const fallbackName = this._buildDocName(doc, i) + '.pdf';
                                const fallbackHandle = await docsDir.getFileHandle(fallbackName);
                                const fallbackFile = await fallbackHandle.getFile();
                                const pdfDataUrl = createPdfObjectUrl(fallbackFile);
                                if (pdfDataUrl) {
                                    recovered.documents.push({
                                        index: i,
                                        pdfBlob: normalizePdfBlob(fallbackFile),
                                        pdfDataUrl,
                                        syncFileName: fallbackName,
                                    });
                                }
                            } catch { /* Both names failed - truly unrecoverable */ }
                        }
                    }
                }
            } catch { /* documents/ subfolder missing */ }

            // Recover videos.
            //
            // FE-MOVED-1 (Peter 2026-06-13): videos are LOCAL_SYNC_REFERENCE
            // only. Since the v5.37.9 de-bloat, sync NO LONGER duplicates the
            // original into the project's videos/ subfolder — the original lives
            // at its import source (FileExplorer source / intake folder). So the
            // old videos/-only lookup recovered nothing for any project synced
            // after the de-bloat, and the "FILES MISSING" banner fired. We now:
            //   1. still try the legacy v4 videos/ copy (back-compat, Rodney's box),
            //   2. fall through to the import source (recorded relative path) and
            //      a bounded filename search of the sync root subtree.
            let legacyVideosDir = null;
            try {
                legacyVideosDir = await projectDir.getDirectoryHandle('videos');
            } catch { /* no videos/ subfolder (post de-bloat) — source search only */ }

            for (let i = 0; i < (videos || []).length; i++) {
                const video = videos[i];
                // A video without any source reference can't be recovered.
                if (video.url || (!video.syncFileName && !video.localSourcePath && !video.quoteMakerSourcePath && !video.relativePath && !video.name)) {
                    continue;
                }
                let recoveredFile = null;

                // 1) Legacy v4 videos/ copy.
                if (legacyVideosDir && video.syncFileName) {
                    try {
                        const fileHandle = await legacyVideosDir.getFileHandle(video.syncFileName);
                        recoveredFile = await fileHandle.getFile();
                    } catch { /* not in videos/ — try the source below */ }
                }

                // 2) Import source (relative path) + filename subtree fallback.
                if (!recoveredFile) {
                    recoveredFile = await this._recoverVideoFromSource(handle, video, projectDir);
                }

                if (recoveredFile) {
                    recovered.videos.push({ index: i, file: recoveredFile, url: URL.createObjectURL(recoveredFile) });
                }
            }

            if (recovered.photos.length || recovered.documents.length || recovered.videos.length) {
                console.log(`[SyncFolder] Recovered: ${recovered.photos.length} photos, ${recovered.documents.length} documents, ${recovered.videos.length} videos from sync folder`);
            }
        } catch (err) {
            console.warn('[SyncFolder] Recovery attempt failed:', err?.message);
        }
        return recovered;
    },

    // ─── Full Project Restore ───────────────────────────────────────────────

    /**
     * Restore a complete project from a sync folder backup.
     * Reads QuoteData.json + all media files from the given directory handle.
     *
     * @param {FileSystemDirectoryHandle} dirHandle - a specific project subfolder
     * @returns {Promise<{project: Object, photoBlobs: Array, learnings: Array}|null>}
     */
    async restoreProjectFromFolder(dirHandle, folderMetadata = {}) {
        if (!dirHandle) return null;

        // 1. QuoteData is the required project snapshot.
        let quoteData;
        try {
            const qdHandle = await dirHandle.getFileHandle('QuoteData.json');
            const qdFile = await qdHandle.getFile();
            quoteData = JSON.parse(await qdFile.text());
        } catch (err) {
            console.error('[SyncFolder] Cannot restore - QuoteData.json missing or invalid:', err?.message);
            return null;
        }

        // 2. Manifest v4/v5 is optional for old backups, but is authoritative
        // for exact media inventory and CRM identity when present.
        let manifest = null;
        try {
            const mfHandle = await dirHandle.getFileHandle('SyncManifest.json');
            const mfFile = await mfHandle.getFile();
            manifest = JSON.parse(await mfFile.text());
        } catch { /* Legacy snapshot without a manifest. */ }

        const manifestKeys = manifest?.keyFields || {};
        const explicitStage = Number(quoteData.currentStage ?? manifestKeys.currentStage);
        const inferredStage = Array.isArray(quoteData.scope) && quoteData.scope.length > 0
            ? 4
            : (quoteData.riskAnalysis ? 2 : 1);
        const currentStage = Number.isInteger(explicitStage) && explicitStage >= 1 && explicitStage <= 5
            ? explicitStage
            : inferredStage;
        const explicitCompletedStages = Array.isArray(quoteData.completedStages)
            ? quoteData.completedStages
                .map(value => Number(value))
                .filter(value => Number.isInteger(value) && value >= 1 && value <= 5)
            : [];
        const completedStages = [...new Set(
            explicitCompletedStages.length > 0
                ? explicitCompletedStages
                : Array.from({ length: Math.max(0, currentStage - 1) }, (_, index) => index + 1),
        )].sort((a, b) => a - b);

        const sourceStatus = String(quoteData.status || manifestKeys.status || 'draft').trim() || 'draft';
        const status = currentStage === 5
            && completedStages.includes(5)
            && sourceStatus.toLowerCase() === 'draft'
            ? 'ready_for_builder_review'
            : sourceStatus;
        const projectId = createRestoreProjectId(manifest?.projectId, quoteData.projectId);
        const formData = { ...(quoteData.formData || {}) };
        const companyId = manifestKeys.companyId
            || quoteData.companyId
            || quoteData.company_id
            || formData.clientCompanyId
            || formData.companyId
            || null;
        const propertyId = manifestKeys.propertyId || quoteData.propertyId || quoteData.property_id || null;
        const contactId = manifestKeys.contactId
            || quoteData.contactId
            || quoteData.contact_id
            || formData.contactId
            || formData.contact_id
            || null;
        const syncRelativePath = normaliseSyncRelativePath(
            folderMetadata?.relativePath
            || quoteData.syncRelativePath
            || quoteData.syncFolderName
            || manifest?.folderName
            || dirHandle.name
            || '',
        );

        // 3. Reconstruct the portable project envelope. pipelineVersion is
        // essential: without it the loader treats a restored five-stage quote
        // with scope but no WMS as a legacy four-stage project and opens Stage 4.
        const project = {
            id: projectId,
            title: quoteData.title || manifest?.displayName || dirHandle.name || 'Restored Project',
            address: formData.address || manifestKeys.siteAddress || '',
            created_at: quoteData.createdAt || quoteData.created_at || quoteData.savedAt || new Date().toISOString(),
            updated_at: new Date().toISOString(),
            currentStage,
            completedStages,
            pipelineVersion: quoteData.pipelineVersion || 'wms-first-v1',
            formData,
            photos: [],
            documents: Array.isArray(formData.documents) ? [...formData.documents] : [],
            videos: [],
            videoTranscripts: [],
            scope: Array.isArray(quoteData.scope) ? quoteData.scope : [],
            quoteData: quoteData.quoteData || null,
            riskAnalysis: quoteData.riskAnalysis || null,
            projectSummary: quoteData.projectSummary || null,
            projectStrategy: quoteData.projectStrategy || null,
            wms: quoteData.wms || null,
            quoteNumber: quoteData.quoteNumber || manifestKeys.quoteRef || '',
            quoteNote: quoteData.quoteNote || '',
            status,
            scopeGstInclusive: quoteData.scopeGstInclusive ?? true,
            defaultMarginPercent: quoteData.defaultMarginPercent || 30,
            syncFolderName: quoteData.syncFolderName || manifest?.folderName || dirHandle.name || '',
            desiredSyncFolderName: quoteData.desiredSyncFolderName || manifest?.desiredFolderName || '',
            syncRelativePath,
            company_id: companyId,
            companyId,
            property_id: propertyId,
            propertyId,
            contact_id: contactId,
            contactId,
            restoredAt: new Date().toISOString(),
            restoredFromSyncFolder: true,
        };

        const warnings = [];
        const allFileEntries = await collectRestoreFileEntries(dirHandle);

        // 4. Restore the manifest's exact photo inventory. v5 backups keep
        // source images in the customer folder/99. QM rather than duplicating
        // them into photos/. Matching uses both manifest filenames and the
        // Photo_NNN ordinal so stale duplicate files are never imported merely
        // because they happen to sort first.
        const photoBlobs = [];
        const imageEntries = allFileEntries.filter(entry => RESTORE_IMAGE_EXTENSION_RE.test(entry.name));
        const manifestPhotos = Array.isArray(manifest?.photos)
            ? [...manifest.photos].sort((a, b) => Number(a?.index ?? 0) - Number(b?.index ?? 0))
            : [];
        const usedPhotoEntries = new Set();

        if (manifestPhotos.length > 0) {
            for (let position = 0; position < manifestPhotos.length; position++) {
                const manifestPhoto = manifestPhotos[position] || {};
                const manifestIndex = Number.isInteger(Number(manifestPhoto.index))
                    ? Number(manifestPhoto.index)
                    : position;
                const entry = selectBestRestoreFile(
                    imageEntries,
                    [manifestPhoto.fileName],
                    { kind: 'photo', ordinal: manifestIndex + 1 },
                    usedPhotoEntries,
                );
                if (!entry) continue;
                try {
                    const file = await entry.handle.getFile();
                    usedPhotoEntries.add(entry);
                    const labelSource = manifestPhoto.fileName || entry.name;
                    const meta = {
                        name: entry.name,
                        originalName: entry.name,
                        manifestFileName: manifestPhoto.fileName || null,
                        aiLabel: labelSource
                            .replace(/^photo[\s_-]*\d+[\s_-]*/i, '')
                            .replace(/^\d+[\s_-]*/, '')
                            .replace(/\.[^.]+$/, '')
                            .replace(/_/g, ' '),
                        mimeType: file.type || 'image/jpeg',
                        sizeKB: Math.max(1, Math.round(file.size / 1024)),
                        sourceRelativePath: entry.relativePath,
                    };
                    const photoIndex = project.photos.length;
                    project.photos.push({
                        id: `restored_photo_${manifestIndex}`,
                        ...meta,
                        blobId: null,
                    });
                    photoBlobs.push({ index: photoIndex, blob: file, meta });
                } catch (err) {
                    console.warn(`[SyncFolder] Failed to read photo ${entry.name}:`, err?.message);
                }
            }
        } else {
            const legacyPhotos = imageEntries.filter(entry => String(entry.relativePath).toLowerCase().startsWith('photos/'));
            const modernPhotos = imageEntries.filter(entry => String(entry.relativePath).toLowerCase().includes('99. qm/'));
            const sortedFallbackPhotos = (legacyPhotos.length > 0 ? legacyPhotos : modernPhotos)
                .sort((a, b) => a.name.localeCompare(b.name));
            const fallbackPhotoLimit = Number(quoteData.photoCount);
            const fallbackPhotos = fallbackPhotoLimit > 0
                ? sortedFallbackPhotos.slice(0, fallbackPhotoLimit)
                : sortedFallbackPhotos;
            for (let i = 0; i < fallbackPhotos.length; i++) {
                const entry = fallbackPhotos[i];
                try {
                    const file = await entry.handle.getFile();
                    const meta = {
                        name: entry.name,
                        originalName: entry.name,
                        aiLabel: entry.name
                            .replace(/^photo[\s_-]*\d+[\s_-]*/i, '')
                            .replace(/^\d+[\s_-]*/, '')
                            .replace(/\.[^.]+$/, '')
                            .replace(/_/g, ' '),
                        mimeType: file.type || 'image/jpeg',
                        sizeKB: Math.max(1, Math.round(file.size / 1024)),
                        sourceRelativePath: entry.relativePath,
                    };
                    const photoIndex = project.photos.length;
                    project.photos.push({ id: `restored_photo_${i}`, ...meta, blobId: null });
                    photoBlobs.push({ index: photoIndex, blob: file, meta });
                } catch (err) {
                    console.warn(`[SyncFolder] Failed to read photo ${entry.name}:`, err?.message);
                }
            }
        }

        const expectedPhotoCount = manifestPhotos.length || Number(quoteData.photoCount) || project.photos.length;
        if (project.photos.length < expectedPhotoCount) {
            warnings.push(`Only ${project.photos.length}/${expectedPhotoCount} photo originals were found.`);
        }

        // 5. Restore every manifest document row, including non-PDF files.
        // Extracted text may live in 99. QM under a human filename rather than
        // the historical *_extracted.txt name; token matching joins the pair.
        // Original bytes are returned separately so App can put them into the
        // durable document blob store before the project record is saved.
        const documentBlobs = [];
        const textEntries = allFileEntries.filter(entry => RESTORE_TEXT_EXTENSION_RE.test(entry.name));
        const originalDocumentEntries = allFileEntries.filter(entry => (
            !RESTORE_TEXT_EXTENSION_RE.test(entry.name)
            && !RESTORE_IMAGE_EXTENSION_RE.test(entry.name)
            && !RESTORE_VIDEO_EXTENSION_RE.test(entry.name)
            && !/\.(?:json|md)$/i.test(entry.name)
        ));
        const manifestDocuments = Array.isArray(manifest?.documents)
            ? [...manifest.documents].sort((a, b) => Number(a?.index ?? 0) - Number(b?.index ?? 0))
            : [];
        let missingDocumentOriginals = 0;
        let missingDocumentText = 0;

        if (manifestDocuments.length > 0) {
            project.documents = [];
            for (let position = 0; position < manifestDocuments.length; position++) {
                const manifestDocument = manifestDocuments[position] || {};
                const documentIndex = Number.isInteger(Number(manifestDocument.index))
                    ? Number(manifestDocument.index)
                    : position;
                const expectedNames = [manifestDocument.originalName, manifestDocument.fileName].filter(Boolean);
                const originalEntry = selectBestRestoreFile(
                    originalDocumentEntries,
                    expectedNames,
                    { kind: 'document' },
                );
                const textEntry = manifestDocument.hasExtractedText || manifestDocument.extractedTextFile
                    ? selectBestRestoreFile(
                        textEntries,
                        [
                            manifestDocument.extractedTextFile,
                            manifestDocument.originalName,
                            manifestDocument.fileName,
                        ],
                        { kind: 'text' },
                    )
                    : null;

                let originalFile = null;
                if (originalEntry) {
                    try {
                        originalFile = await originalEntry.handle.getFile();
                    } catch (err) {
                        console.warn(`[SyncFolder] Failed to read document ${originalEntry.name}:`, err?.message);
                    }
                }

                let content = '';
                if (textEntry) {
                    try {
                        const textFile = await textEntry.handle.getFile();
                        content = stripExtractedTextHeader(await textFile.text());
                    } catch (err) {
                        console.warn(`[SyncFolder] Failed to read extracted text ${textEntry.name}:`, err?.message);
                    }
                }

                if (!originalFile) missingDocumentOriginals += 1;
                if ((manifestDocument.hasExtractedText || manifestDocument.extractedTextFile) && !content.trim()) {
                    missingDocumentText += 1;
                }

                const name = manifestDocument.originalName
                    || manifestDocument.fileName
                    || `Restored document ${documentIndex + 1}`;
                const ext = restoreFileExtension(name);
                const classification = manifestDocument.class || 'unknown';
                const restoredDocument = {
                    id: `restored_doc_${documentIndex}`,
                    name,
                    originalName: name,
                    ext,
                    type: classification,
                    class: classification,
                    aiClassification: classification,
                    confidence: manifestDocument.confidence ?? null,
                    evidence: manifestDocument.evidence || null,
                    classifiedAt: manifestDocument.classifiedAt || null,
                    contentType: manifestDocument.contentType
                        || originalFile?.type
                        || (ext === 'pdf' ? 'application/pdf' : null),
                    content,
                    size: originalFile?.size || 0,
                    syncFileName: manifestDocument.fileName || null,
                    extractedTextFile: manifestDocument.extractedTextFile || null,
                    hasExtractedText: !!content.trim(),
                    hasOriginalFile: !!originalFile,
                    sourceRelativePath: originalEntry?.relativePath || null,
                    sourceFileMetadataId: manifestDocument.fileMetadataId || null,
                    sourcePdfS3Key: manifestDocument.pdfS3Key || null,
                    docBlobId: null,
                };
                const restoredIndex = project.documents.length;
                project.documents.push(restoredDocument);
                if (originalFile) {
                    documentBlobs.push({
                        index: restoredIndex,
                        blob: originalFile,
                        meta: {
                            name,
                            mimeType: restoredDocument.contentType || originalFile.type || 'application/octet-stream',
                            sourceRelativePath: originalEntry.relativePath,
                        },
                    });
                }
            }
        } else {
            // v4 back-compat: originals and *_extracted.txt sidecars live in
            // documents/. Preserve non-PDF files too.
            const legacyDocuments = originalDocumentEntries
                .filter(entry => String(entry.relativePath).toLowerCase().startsWith('documents/'))
                .sort((a, b) => a.name.localeCompare(b.name));
            if (legacyDocuments.length > 0) {
                project.documents = [];
                for (let i = 0; i < legacyDocuments.length; i++) {
                    const entry = legacyDocuments[i];
                    try {
                        const file = await entry.handle.getFile();
                        const textEntry = selectBestRestoreFile(
                            textEntries,
                            [entry.name.replace(/\.[^.]+$/, '_extracted.txt'), entry.name],
                            { kind: 'text' },
                        );
                        let content = '';
                        if (textEntry) {
                            const textFile = await textEntry.handle.getFile();
                            content = stripExtractedTextHeader(await textFile.text());
                        }
                        const ext = restoreFileExtension(entry.name);
                        const restoredIndex = project.documents.length;
                        project.documents.push({
                            id: `restored_doc_${i}`,
                            name: entry.name,
                            originalName: entry.name,
                            ext,
                            type: ext === 'pdf' ? 'reference' : 'unknown',
                            contentType: file.type || (ext === 'pdf' ? 'application/pdf' : null),
                            content,
                            size: file.size,
                            syncFileName: entry.name,
                            hasExtractedText: !!content.trim(),
                            hasOriginalFile: true,
                            sourceRelativePath: entry.relativePath,
                            docBlobId: null,
                        });
                        documentBlobs.push({
                            index: restoredIndex,
                            blob: file,
                            meta: {
                                name: entry.name,
                                mimeType: file.type || 'application/octet-stream',
                                sourceRelativePath: entry.relativePath,
                            },
                        });
                    } catch (err) {
                        console.warn(`[SyncFolder] Failed to read document ${entry.name}:`, err?.message);
                    }
                }
            }
        }

        const expectedDocumentCount = manifestDocuments.length
            || Number(quoteData.documentCount)
            || project.documents.length;
        if (project.documents.length < expectedDocumentCount) {
            warnings.push(`Only ${project.documents.length}/${expectedDocumentCount} document records were restored.`);
        }
        if (missingDocumentOriginals > 0) {
            warnings.push(`${missingDocumentOriginals} document original(s) were unavailable; metadata and extracted text were retained.`);
        }
        if (missingDocumentText > 0) {
            warnings.push(`${missingDocumentText} document text extraction(s) were unavailable.`);
        }

        // 6. Restore source videos from either the legacy videos/ folder or the
        // v5 manifest's originalName/fileName references within the job tree.
        const videoEntries = allFileEntries.filter(entry => RESTORE_VIDEO_EXTENSION_RE.test(entry.name));
        const transcriptEntries = textEntries.filter(entry => /_transcript\.txt$/i.test(entry.name));
        const videoBlobs = [];
        const manifestVideos = Array.isArray(manifest?.videos)
            ? [...manifest.videos].sort((a, b) => Number(a?.index ?? 0) - Number(b?.index ?? 0))
            : [];
        const videoRows = manifestVideos.length > 0
            ? manifestVideos
            : videoEntries
                .filter(entry => String(entry.relativePath).toLowerCase().startsWith('videos/'))
                .map((entry, index) => ({
                    index,
                    fileName: entry.name,
                    originalName: entry.name,
                    transcriptFile: `Video_${index + 1}_transcript.txt`,
                }));
        const expectedVideoCount = videoRows.length;

        for (let position = 0; position < videoRows.length; position++) {
            const videoRow = videoRows[position] || {};
            const videoIndex = Number.isInteger(Number(videoRow.index)) ? Number(videoRow.index) : position;
            const entry = selectBestRestoreFile(
                videoEntries,
                [videoRow.originalName, videoRow.fileName],
                { kind: 'document' },
            );
            if (!entry) continue;
            try {
                const videoFile = await entry.handle.getFile();
                const transcriptEntry = videoRow.transcriptFile
                    ? selectBestRestoreFile(transcriptEntries, [videoRow.transcriptFile], { kind: 'text' })
                    : null;
                let transcript = '';
                if (transcriptEntry) {
                    const transcriptFile = await transcriptEntry.handle.getFile();
                    transcript = stripExtractedTextHeader(await transcriptFile.text());
                }
                const restoredVideo = {
                    id: `restored_video_${videoIndex}`,
                    name: videoRow.originalName || entry.name,
                    syncFileName: videoRow.fileName || entry.name,
                    size: videoFile.size,
                    processed: !!transcript,
                    transcript,
                    sourceRelativePath: entry.relativePath,
                    videoBlobId: null,
                };
                const restoredIndex = project.videos.length;
                project.videos.push(restoredVideo);
                videoBlobs.push({
                    index: restoredIndex,
                    blob: videoFile,
                    meta: {
                        name: restoredVideo.name,
                        mimeType: videoFile.type || 'video/mp4',
                        sourceRelativePath: entry.relativePath,
                    },
                });
            } catch (err) {
                console.warn(`[SyncFolder] Failed to read video ${entry.name}:`, err?.message);
            }
        }

        if (expectedVideoCount > project.videos.length) {
            warnings.push(`Only ${project.videos.length}/${expectedVideoCount} video originals were found.`);
        }

        // 7. Inspector notes supplement old snapshots that did not embed notes.
        try {
            const notesHandle = await dirHandle.getFileHandle('Inspector_Notes.txt');
            const notesFile = await notesHandle.getFile();
            const notesContent = stripExtractedTextHeader(await notesFile.text());
            if (notesContent.trim() && !project.formData.notes) {
                project.formData.notes = notesContent;
            }
        } catch { /* No notes file. */ }

        // 8. v5 learnings live at root; v4 learnings remain readable.
        let learnings = [];
        try {
            const learningsHandle = await dirHandle.getFileHandle('AI_Learnings.json');
            const learningsFile = await learningsHandle.getFile();
            const learningsData = JSON.parse(await learningsFile.text());
            learnings = learningsData.learnings || [];
        } catch {
            try {
                const learningsDir = await dirHandle.getDirectoryHandle('learnings');
                const learningsHandle = await learningsDir.getFileHandle('AI_Learnings.json');
                const learningsFile = await learningsHandle.getFile();
                const learningsData = JSON.parse(await learningsFile.text());
                learnings = learningsData.learnings || [];
            } catch { /* No learnings. */ }
        }

        const diagnostics = {
            expectedPhotos: expectedPhotoCount,
            restoredPhotos: project.photos.length,
            expectedDocuments: expectedDocumentCount,
            restoredDocuments: project.documents.length,
            restoredDocumentOriginals: documentBlobs.length,
            expectedVideos: expectedVideoCount,
            restoredVideos: project.videos.length,
            restoredVideoOriginals: videoBlobs.length,
            warnings,
        };

        console.log(`[SyncFolder] Restored project "${project.title}": ${photoBlobs.length}/${expectedPhotoCount} photos, ${project.documents.length}/${expectedDocumentCount} docs (${documentBlobs.length} originals), ${project.videos.length} videos, ${learnings.length} learnings`);
        return { project, photoBlobs, documentBlobs, videoBlobs, learnings, diagnostics };
    },

    /**
     * Scan the sync folder root for restorable project folders.
     * Returns metadata for each folder that contains a QuoteData.json.
     *
     * @returns {Promise<Array<{name: string, title: string, quoteNumber: string, address: string, savedAt: string, handle: FileSystemDirectoryHandle}>>}
     */
    async scanProjectFolders() {
        try {
            if (!await this.ensurePermission()) return [];
            const handle = await this.getHandle();
            if (!handle) return [];

            const folders = [];
            let visited = 0;
            const scan = async (dirHandle, relativePath = '', depth = 0) => {
                if (!dirHandle || visited >= MAX_PROJECT_DIRECTORY_SCAN_COUNT) return;
                visited += 1;

                const data = await this._readQuoteData(dirHandle);
                if (data) {
                    const manifest = await this._readSyncManifest(dirHandle);
                    const fd = data.formData || {};
                    folders.push({
                        name: dirHandle.name,
                        relativePath,
                        projectId: data.projectId || manifest?.projectId || '',
                        title: data.title || dirHandle.name,
                        quoteNumber: data.quoteNumber || '',
                        address: fd.address || '',
                        clientCompany: fd.clientCompany || fd.clientName || '',
                        strataPlan: fd.strataplan || fd.strataPlan || fd.strataNumber || '',
                        savedAt: data.savedAt || '',
                        photoCount: data.photoCount || 0,
                        documentCount: data.documentCount || 0,
                        handle: dirHandle,
                    });
                    return;
                }

                if (depth >= MAX_PROJECT_DIRECTORY_SCAN_DEPTH) return;
                for await (const entry of this._childDirectories(dirHandle)) {
                    if (visited >= MAX_PROJECT_DIRECTORY_SCAN_COUNT) break;
                    if (!entry || entry.kind !== 'directory') continue;
                    if (SYNC_SCAN_IGNORED_DIRECTORY_NAMES.has(String(entry.name || '').toLowerCase())) continue;
                    await scan(entry, joinSyncRelativePath(relativePath, entry.name), depth + 1);
                }
            };

            await scan(handle, '', 0);

            // Sort newest first
            folders.sort((a, b) => (b.savedAt || '').localeCompare(a.savedAt || ''));
            return folders;
        } catch (err) {
            console.warn('[SyncFolder] Folder scan failed:', err?.message);
            return [];
        }
    },

    // ========================================================================
    // AGENT-FACING READ METHODS - used by the QuoteMaker Assistant tools
    // `list_project_files` and `read_project_file` so the agent can see what's
    // in the user's local sync folder for a given project. Agent never writes;
    // sync direction is one-way (app → folder via syncProject above).
    // ========================================================================

    /**
     * List every file in a project's sync folder as a flat tree.
     * Returns: [{ relativePath, kind, sizeBytes, lastModified }]
     * - relativePath: 'photos/001_roof.jpg', 'documents/RFQ.pdf', etc.
     * - kind: top-level subdirectory ('photos' | 'documents' | 'videos' | 'learnings' | 'other')
     * - sizeBytes: file.size (for budgeting)
     * - lastModified: file.lastModified (for stale detection)
     *
     * Returns [] if the sync folder isn't configured, isn't reachable, or
     * the project has never been synced. Capped at 500 entries to keep
     * agent context budgets sane.
     */
    async _listProjectFolderTreeFromDirectory(projectDir) {
        if (!projectDir) return [];
        const MAX_ENTRIES = 500;
        const entries = [];
        const KIND_DIRS = ['photos', 'documents', 'videos', 'learnings'];

        // Walk the known top-level subdirectories first
        for (const kind of KIND_DIRS) {
            try {
                const subdir = await projectDir.getDirectoryHandle(kind);
                for await (const [name, h] of subdir.entries()) {
                    if (entries.length >= MAX_ENTRIES) break;
                    if (h.kind !== 'file') continue;
                    try {
                        const file = await h.getFile();
                        entries.push({
                            relativePath: `${kind}/${name}`,
                            kind,
                            sizeBytes: file.size,
                            lastModified: file.lastModified,
                        });
                    } catch { /* skip unreadable files */ }
                }
            } catch { /* subdir doesn't exist - skip */ }
        }

        // Also include top-level project files (QuoteData.json, SyncManifest.json, notes.txt, etc.)
        // v5 (de-bloat): AI_Learnings.json now lives at project root — classify it as 'learnings'
        // for consistency with the v4 nested layout's `kind` reporting.
        try {
            for await (const [name, h] of projectDir.entries()) {
                if (entries.length >= MAX_ENTRIES) break;
                if (h.kind !== 'file') continue;
                try {
                    const file = await h.getFile();
                    entries.push({
                        relativePath: name,
                        kind: name === 'AI_Learnings.json' ? 'learnings' : 'other',
                        sizeBytes: file.size,
                        lastModified: file.lastModified,
                    });
                } catch { /* skip unreadable files */ }
            }
        } catch { /* shouldn't happen but be defensive */ }

        return entries;
    },

    async listProjectFolderTree(projectRecord) {
        if (!projectRecord) return [];
        if (!await this.ensurePermission()) return [];
        const handle = await this.getHandle();
        if (!handle) return [];

        const dirInfo = await this._getProjectDirectory(handle, projectRecord, { create: false });
        if (!dirInfo) return []; // Project never synced
        return this._listProjectFolderTreeFromDirectory(dirInfo.projectDir);
    },

    async searchProjectFolderFiles(options = {}) {
        const folders = await this.scanProjectFolders();
        if (!Array.isArray(folders) || folders.length === 0) return [];

        const query = String(options.query || '').trim().toLowerCase();
        const kind = String(options.kind || '').trim().toLowerCase();
        const projectIds = new Set((Array.isArray(options.projectIds) ? options.projectIds : [])
            .map(v => String(v || '').trim().toLowerCase())
            .filter(Boolean));
        const clientCompany = String(options.clientCompany || '').trim().toLowerCase();
        const strataPlan = String(options.strataPlan || '').replace(/\s+/g, '').toLowerCase();
        const address = String(options.address || '').trim().toLowerCase();
        const limit = Math.max(1, Math.min(200, Number(options.limit || 50) || 50));

        const folderMatches = (folder) => {
            const projectId = String(folder.projectId || '').trim().toLowerCase();
            if (projectIds.size) return projectIds.has(projectId);
            if (clientCompany && !String(folder.clientCompany || '').toLowerCase().includes(clientCompany)) return false;
            if (strataPlan && String(folder.strataPlan || '').replace(/\s+/g, '').toLowerCase() !== strataPlan) return false;
            if (address && !String(folder.address || '').toLowerCase().includes(address)) return false;
            return true;
        };

        const results = [];
        for (const folder of folders) {
            if (!folderMatches(folder)) continue;
            const files = await this._listProjectFolderTreeFromDirectory(folder.handle);
            for (const file of files) {
                if (results.length >= limit) break;
                if (kind && String(file.kind || '').toLowerCase() !== kind) continue;
                const haystack = [
                    folder.name,
                    folder.title,
                    folder.quoteNumber,
                    folder.address,
                    folder.clientCompany,
                    folder.strataPlan,
                    file.relativePath,
                    file.kind,
                ].filter(Boolean).join(' ').toLowerCase();
                if (query && !haystack.includes(query)) continue;
                results.push({
                    projectId: folder.projectId || '',
                    folderName: folder.name,
                    title: folder.title,
                    quoteNumber: folder.quoteNumber,
                    address: folder.address,
                    clientCompany: folder.clientCompany,
                    strataPlan: folder.strataPlan,
                    relativePath: file.relativePath,
                    kind: file.kind,
                    sizeBytes: file.sizeBytes,
                    lastModified: file.lastModified,
                });
            }
            if (results.length >= limit) break;
        }
        return results;
    },

    /**
     * Read a project sync-folder file as text. Returns null if the file is
     * missing, binary, or larger than `maxBytes` (default 200KB). Agent uses
     * this to read inspector notes, RFQ extracts, learnings JSON, etc.
     *
     * SAFETY: Path is validated to prevent traversal - the relativePath must
     * be a simple `subdir/filename` shape with no `..`, no absolute paths,
     * no leading slashes.
     *
     * @param {Object} projectRecord
     * @param {string} relativePath - e.g. 'documents/RFQ.txt'
     * @param {number} [maxBytes=200_000] - reject files larger than this
     * @returns {Promise<{ text: string, sizeBytes: number, mimeType: string } | null>}
     */
    async _readTextFileFromDirectory(projectDir, relativePath, maxBytes = 200_000) {
        if (!projectDir || !relativePath || typeof relativePath !== 'string') return null;
        // Path traversal guard - must be 'subdir/file' or 'file' only
        if (relativePath.includes('..') || relativePath.startsWith('/') || relativePath.startsWith('\\') || /[A-Z]:[\\/]/i.test(relativePath)) {
            console.warn('[SyncFolder] Rejected suspicious file path:', relativePath);
            return null;
        }

        // Walk the path segments
        const segments = relativePath.split('/').filter(Boolean);
        if (segments.length === 0) return null;
        let dir = projectDir;
        for (let i = 0; i < segments.length - 1; i++) {
            try { dir = await dir.getDirectoryHandle(segments[i]); }
            catch { return null; }
        }
        let fileHandle;
        try { fileHandle = await dir.getFileHandle(segments[segments.length - 1]); }
        catch { return null; }

        const file = await fileHandle.getFile();
        if (file.size > maxBytes) {
            return {
                text: `[FILE TOO LARGE: ${(file.size / 1024).toFixed(1)} KB exceeds ${(maxBytes / 1024).toFixed(0)} KB cap]`,
                sizeBytes: file.size,
                mimeType: file.type || 'application/octet-stream',
                truncated: true,
            };
        }
        // Read as text. Binary files will produce mojibake; the caller should
        // gate by mimeType / file extension before invoking us.
        const text = await file.text();
        return {
            text,
            sizeBytes: file.size,
            mimeType: file.type || 'text/plain',
            truncated: false,
        };
    },

    async readProjectTextFile(projectRecord, relativePath, maxBytes = 200_000) {
        if (!projectRecord || !relativePath || typeof relativePath !== 'string') return null;
        if (!await this.ensurePermission()) return null;
        const handle = await this.getHandle();
        if (!handle) return null;

        const dirInfo = await this._getProjectDirectory(handle, projectRecord, { create: false });
        if (!dirInfo) return null;
        return this._readTextFileFromDirectory(dirInfo.projectDir, relativePath, maxBytes);
    },

    async readProjectTextFileFromFolder({ projectId = '', folderName = '', relativePath = '' } = {}, maxBytes = 200_000) {
        if (!relativePath) return null;
        const folders = await this.scanProjectFolders();
        const wantProjectId = String(projectId || '').trim().toLowerCase();
        const wantFolderName = String(folderName || '').trim().toLowerCase();
        const folder = folders.find(f => wantProjectId && String(f.projectId || '').trim().toLowerCase() === wantProjectId)
            || folders.find(f => wantFolderName && String(f.name || '').trim().toLowerCase() === wantFolderName);
        if (!folder?.handle) return null;
        return this._readTextFileFromDirectory(folder.handle, relativePath, maxBytes);
    },

    /**
     * Read a project file as a raw File object — for non-text formats like
     * PDFs where the assistant tool handler needs the binary to run through
     * extractPdfText() (4-strategy text extraction + vision-OCR fallback for
     * scanned PDFs without selectable text).
     *
     * @param {FileSystemDirectoryHandle} projectDir
     * @param {string} relativePath
     * @returns {Promise<{ file: File, sizeBytes: number, mimeType: string } | null>}
     */
    async _readFileBlobFromDirectory(projectDir, relativePath) {
        if (!projectDir || !relativePath || typeof relativePath !== 'string') return null;
        if (relativePath.includes('..') || relativePath.startsWith('/') || relativePath.startsWith('\\') || /[A-Z]:[\\/]/i.test(relativePath)) {
            console.warn('[SyncFolder] Rejected suspicious file path:', relativePath);
            return null;
        }
        const segments = relativePath.split('/').filter(Boolean);
        if (segments.length === 0) return null;
        let dir = projectDir;
        for (let i = 0; i < segments.length - 1; i++) {
            try { dir = await dir.getDirectoryHandle(segments[i]); }
            catch { return null; }
        }
        let fileHandle;
        try { fileHandle = await dir.getFileHandle(segments[segments.length - 1]); }
        catch { return null; }
        const file = await fileHandle.getFile();
        return { file, sizeBytes: file.size, mimeType: file.type || 'application/octet-stream' };
    },

    async readProjectFileBlob(projectRecord, relativePath) {
        if (!projectRecord || !relativePath || typeof relativePath !== 'string') return null;
        if (!await this.ensurePermission()) return null;
        const handle = await this.getHandle();
        if (!handle) return null;
        const dirInfo = await this._getProjectDirectory(handle, projectRecord, { create: false });
        if (!dirInfo) return null;
        return this._readFileBlobFromDirectory(dirInfo.projectDir, relativePath);
    },

    async readProjectFileBlobFromFolder({ projectId = '', folderName = '', relativePath = '' } = {}) {
        if (!relativePath) return null;
        const folders = await this.scanProjectFolders();
        const wantProjectId = String(projectId || '').trim().toLowerCase();
        const wantFolderName = String(folderName || '').trim().toLowerCase();
        const folder = folders.find(f => wantProjectId && String(f.projectId || '').trim().toLowerCase() === wantProjectId)
            || folders.find(f => wantFolderName && String(f.name || '').trim().toLowerCase() === wantFolderName);
        if (!folder?.handle) return null;
        return this._readFileBlobFromDirectory(folder.handle, relativePath);
    },
};
